import React, { useState, useRef, useEffect } from "react";
import {
  NodeDefinition,
  MakinaProcess,
  NodeElement,
  NodeType,
  ProcessDefinition,
  RegsitryLink,
  SaveJsonResult,
  BaseNodeDefinition,
  ActivityNodeDefinition,
  LinkDefinition,
} from "../interface";
import BpmnModeler from "bpmn-js/lib/Modeler";
import $ from "jquery";
// @ts-ignore
import "./style.css";
// @ts-ignore
import "bpmn-js/dist/assets/diagram-js.css";
// @ts-ignore
import "bpmn-js/dist/assets/bpmn-js.css";
// @ts-ignore
import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
// @ts-ignore
import "@bpmn-io/properties-panel/assets/properties-panel.css";
import {
  BpmnPropertiesPanelModule,
  BpmnPropertiesProviderModule,
} from "bpmn-js-properties-panel";
import caseModdleExtension from "../descriptors/case.json";
import CaseModule from "../provider/case";
import { ModdleElement } from "bpmn-js/lib/BaseModeler";
import {
  resolveNodeBpmnMapping,
  SIMPLE_NODE_SERVICE_MAP,
  NODE_TYPE_MAP,
} from "./nodeServiceMap";
import { createFormPaletteModule } from "./DynamicFormBuilder";
import {
  applyFullLayout,
  fitDiagramToViewport,
} from "./bpmn-layout-improvements";

const SENSITIVE_SERVICES = ["JsNode", "SQLNode", "RestNode"];
const READONLY_STYLE_ID = "sensitive-readonly-style";

const BPMN_TYPE_TO_SERVICE: Record<string, string> = {
  "bpmn:UserTask": "SimpleActivityNode",
  "bpmn:ServiceTask": "SimpleServiceNode",
  "bpmn:ScriptTask": "JsNode",
  "bpmn:ExclusiveGateway": "IfNode",
  "bpmn:InclusiveGateway": "CaseWhenNode",
  "bpmn:ParallelGateway": "GatewayNode",
  "bpmn:SendTask": "SendEmailNode",
  "bpmn:BusinessRuleTask": "DecisionRulesNode",
  "bpmn:CallActivity": "SubcaseNode",
  "bpmn:IntermediateCatchEvent": "WaitDelayNode",
  "bpmn:IntermediateThrowEvent": "PublishMessageNode",
  "bpmn:EndEvent": "EndNode",
};

function enrichNodeFromBpmnType(
  nodeCode: string,
  bpmnType: string,
  nodeLabel: string,
  isFirstActivityNode: boolean,
  existingNode: any = {},
): any {
  const service = existingNode.service || BPMN_TYPE_TO_SERVICE[bpmnType] || "";
  const mapping =
    SIMPLE_NODE_SERVICE_MAP[service] ||
    NODE_TYPE_MAP[existingNode.type || ""] ||
    null;
  const defaultParams = mapping?.defaultParams ?? {};

  const isActivity = bpmnType === "bpmn:UserTask" || bpmnType === "bpmn:Task";
  const isSimple =
    bpmnType === "bpmn:ScriptTask" ||
    bpmnType === "bpmn:ExclusiveGateway" ||
    bpmnType === "bpmn:InclusiveGateway" ||
    bpmnType === "bpmn:ParallelGateway" ||
    bpmnType === "bpmn:IntermediateCatchEvent" ||
    bpmnType === "bpmn:IntermediateThrowEvent" ||
    bpmnType === "bpmn:SendTask" ||
    bpmnType === "bpmn:BusinessRuleTask" ||
    bpmnType === "bpmn:CallActivity" ||
    bpmnType === "bpmn:SubProcess";
  const isService = bpmnType === "bpmn:ServiceTask";
  const isGateway =
    bpmnType === "bpmn:ExclusiveGateway" ||
    bpmnType === "bpmn:InclusiveGateway" ||
    bpmnType === "bpmn:ParallelGateway";

  const nodeType = isActivity
    ? NodeType.activity
    : isService
      ? NodeType.service
      : NodeType.simple;

  const formCode = isActivity
    ? `formulaire${nodeCode.charAt(0).toUpperCase() + nodeCode.slice(1)}`
    : undefined;

  const outputs: { code: string; label: string }[] = isGateway
    ? [
        { code: "yes", label: "Oui" },
        { code: "no", label: "Non" },
      ]
    : mapping?.outputs
      ? mapping.outputs.map((o: string) => ({ code: o, label: "" }))
      : [{ code: "output", label: "" }];

  const inputs: { code: string; label: string }[] =
    isFirstActivityNode && isActivity ? [] : [{ code: "input", label: "" }];

  const enriched: any = {
    code: nodeCode,
    label: nodeLabel,
    type: nodeType,
    service,
    start: isFirstActivityNode && isActivity,
    icon: isActivity ? "Task" : "",
    params:
      existingNode.params && Object.keys(existingNode.params).length > 0
        ? existingNode.params
        : defaultParams,
    inputs,
    outputs,
    caseContext: existingNode.caseContext || {},
    initContext: existingNode.initContext || {},
    caseStatus:
      existingNode.caseStatus || (isFirstActivityNode ? "En-cours" : {}),
    caseOwner: existingNode.caseOwner || "",
    caseRelationSettings: existingNode.caseRelationSettings || {},
    retry: existingNode.retry || {},
    startCondition: existingNode.startCondition || {},
    resumeCondition: existingNode.resumeCondition || {},
    resume: existingNode.resume || false,
    task: existingNode.task || false,
    data: existingNode.data || {},
    owner: existingNode.owner || (isActivity ? "{{token.user.email}}" : ""),
    group: existingNode.group || "",
    codeTemplate: existingNode.codeTemplate || "",
    dataTemplate: existingNode.dataTemplate || "",
    dueAtDuration: existingNode.dueAtDuration || {},
    actions: existingNode.actions || {},
    actionParams:
      existingNode.actionParams ||
      (isActivity
        ? {
            submit: {
              label: isFirstActivityNode
                ? "Soumettre"
                : `Valider — ${nodeLabel}`,
            },
          }
        : {}),
  };

  if (isActivity && formCode) {
    enriched.form = isFirstActivityNode ? formCode : [{ code: formCode }];
    enriched.caseContext = {
      ...enriched.caseContext,
      [nodeCode]: "{{data}}",
    };
  }

  if (existingNode.position) {
    enriched.position = existingNode.position;
  }

  return enriched;
}

function enrichEndNodeFromBpmnType(
  nodeCode: string,
  nodeLabel: string,
  existingNode: any = {},
): any {
  return {
    id: nodeCode,
    code: nodeCode,
    type: "endEvent",
    service: "EndNode",
    label: nodeLabel,
    params: { stopAll: true },
    caseStatus: { status: nodeLabel.toLowerCase().replace(/\s+/g, "-") },
    caseContext: {},
    inputs: [{ code: "input", label: "" }],
    outputs: [],
    ...(existingNode.position ? { position: existingNode.position } : {}),
  };
}

function buildDefaultForms(nodes: Record<string, any>): Record<string, any> {
  const forms: Record<string, any> = {};
  for (const [code, node] of Object.entries(nodes)) {
    if (node.type !== NodeType.activity) continue;
    const formCode = `formulaire${code.charAt(0).toUpperCase() + code.slice(1)}`;
    const label = node.label || code;

    const properties: Record<string, any> = {};
    const uiElements: any[] = [];

    const addField = (
      fieldCode: string,
      fieldLabel: string,
      fieldType: string = "string",
    ) => {
      properties[fieldCode] = { type: fieldType, title: fieldLabel };
      const control: any = {
        type: "Control",
        label: fieldLabel,
        scope: `#/properties/${fieldCode}`,
        options: { grid: { md: 12, sm: 12, xs: 12 } },
      };
      if (fieldType === "number") {
        control.options = buildNumberControlOptions(12, 12, 12);
      }
      uiElements.push({ type: "HorizontalLayout", elements: [control] });
    };

    const lowerLabel = label.toLowerCase();
    if (
      lowerLabel.includes("document") ||
      lowerLabel.includes("manquant") ||
      lowerLabel.includes("missing") ||
      lowerLabel.includes("fournir")
    ) {
      addField("documentsManquants", "Documents manquants");
      addField("delai", "Délai de transmission");
      addField("commentaire", "Commentaire");
    } else if (
      lowerLabel.includes("submit") ||
      lowerLabel.includes("soumettre") ||
      lowerLabel.includes("demande")
    ) {
      addField("nom", "Nom");
      addField("prenom", "Prénom");
      addField("email", "E-mail");
      addField("telephone", "Téléphone");
      addField("message", "Message");
    } else if (
      lowerLabel.includes("verif") ||
      lowerLabel.includes("vérif") ||
      lowerLabel.includes("complet")
    ) {
      addField("dossierComplet", "Dossier complet ?", "boolean");
      addField("commentaire", "Commentaire du conseiller");
    } else if (
      lowerLabel.includes("risk") ||
      lowerLabel.includes("risque") ||
      lowerLabel.includes("analys")
    ) {
      addField("niveauRisque", "Niveau de risque");
      addField("scoreSolvabilite", "Score de solvabilité", "number");
      addField("tauxEndettement", "Taux d'endettement (%)", "number");
      addField("risqueAcceptable", "Risque acceptable ?", "boolean");
      addField("commentaire", "Commentaire analyste");
    } else if (
      lowerLabel.includes("sign") ||
      lowerLabel.includes("contrat") ||
      lowerLabel.includes("signer")
    ) {
      addField("contratSigne", "Contrat signé ?", "boolean");
      addField("dateSignature", "Date de signature");
      addField("commentaire", "Commentaire");
    } else if (
      lowerLabel.includes("offre") ||
      lowerLabel.includes("offer") ||
      lowerLabel.includes("prepar")
    ) {
      addField("montantAccorde", "Montant accordé", "number");
      addField("tauxInteret", "Taux d'intérêt (%)", "number");
      addField("mensualite", "Mensualité", "number");
      addField("duree", "Durée (mois)", "number");
      addField("conditions", "Conditions");
    } else if (
      lowerLabel.includes("decis") ||
      lowerLabel.includes("décis") ||
      lowerLabel.includes("valid") ||
      lowerLabel.includes("approv") ||
      lowerLabel.includes("crédit") ||
      lowerLabel.includes("credit")
    ) {
      addField("decision", "Décision (Approuvé/Refusé)");
      addField("montantAccorde", "Montant accordé", "number");
      addField("tauxInteret", "Taux d'intérêt (%)", "number");
      addField("commentaire", "Commentaire");
    } else if (
      lowerLabel.includes("refus") ||
      lowerLabel.includes("notif") ||
      lowerLabel.includes("reject")
    ) {
      addField("motifRefus", "Motif du refus");
      addField("commentaire", "Commentaire");
    } else if (
      lowerLabel.includes("fund") ||
      lowerLabel.includes("fond") ||
      lowerLabel.includes("virement") ||
      lowerLabel.includes("reception") ||
      lowerLabel.includes("réception") ||
      lowerLabel.includes("confirm")
    ) {
      addField("fondsRecus", "Fonds reçus ?", "boolean");
      addField("montantRecu", "Montant reçu", "number");
      addField("dateReception", "Date de réception");
      addField("commentaire", "Commentaire");
    } else {
      addField("resultat", "Résultat");
      addField("commentaire", "Commentaire");
    }

    forms[formCode] = {
      id: formCode,
      code: formCode,
      label,
      schema: {
        type: "object",
        properties,
        required: [],
      },
      uischema: {
        type: "Categorization",
        elements: [
          {
            type: "Category",
            label,
            elements: [
              {
                type: "Group",
                options: { variant: "paper" },
                elements: uiElements,
              },
            ],
          },
        ],
      },
    };
  }
  return forms;
}

function buildDefaultFiltersAndCategory(
  processCode: string,
  nodes: Record<string, any>,
  processLabel?: string,
): { filterCategory: any[]; filters: any[] } {
  const firstActivityCode = Object.keys(nodes).find(
    (k) => nodes[k].type === NodeType.activity && nodes[k].start === true,
  );

  const caseColumns: any[] = [
    {
      type: "text",
      label: "Code",
      key: "code",
      filters: ["equals"],
      compact: true,
    },
    {
      type: "dateTime",
      label: "Date de création",
      key: "createdAt",
      filters: ["gt", "lt"],
      compact: true,
    },
    {
      type: "dateTime",
      label: "Date de mise à jour",
      key: "updatedAt",
      filters: ["gt", "lt"],
    },
    {
      type: "text",
      label: "Statut",
      key: "status",
      filters: ["equals"],
      compact: true,
    },
    {
      type: "text",
      label: "Assigné à",
      key: "owner",
      filters: ["contains"],
      compact: true,
    },
  ];

  if (firstActivityCode) {
    caseColumns.splice(1, 0, {
      type: "text",
      label: "Créé par",
      key: `context.node.${firstActivityCode}.user.fullname`,
      compact: true,
      compactTitle: true,
    });
  }

  const filterCategory = [
    {
      code: `filterCategory-${processCode}`,
      label: processLabel || processCode,
      rank: "1",
      icon: "FolderOutlined",
      filterCategoryColor: "#1565C0",
      condition: {},
      parentCategoryCode: null,
    },
  ];

  const filters = [
    {
      code: `filterCase-${processCode}`,
      label: "Dossiers",
      icon: "FolderOutlined",
      rank: 1,
      filterCategoryCode: `filterCategory-${processCode}`,
      filterColor: "#1565C0",
      condition: {},
      schema: {
        type: "case",
        crudQuery: {
          joins: ["processVersion.process", "nodeContexts"],
          where: { processVersion: { processCode } },
          orderBy: [{ updatedAt: "desc" }],
        },
        columns: caseColumns,
      },
    },
    {
      code: `filterActivity-${processCode}`,
      label: "Activités",
      icon: "TaskOutlined",
      rank: 2,
      filterCategoryCode: `filterCategory-${processCode}`,
      filterColor: "#388E3C",
      condition: {},
      schema: {
        type: "activity",
        crudQuery: {
          joins: ["token.nodeContext"],
          where: {
            token: {
              case: {
                processVersion: { processCode },
              },
            },
          },
          orderBy: [{ updatedAt: "desc" }],
        },
        columns: [
          {
            type: "text",
            label: "Code",
            key: "code",
            filters: ["equals"],
            compact: true,
          },
          {
            type: "text",
            label: "Étape",
            key: "token.targetNodeCode",
            filters: ["equals"],
            compact: true,
          },
          {
            type: "text",
            label: "Assigné à",
            key: "owner",
            filters: ["contains"],
            compact: true,
            compactTitle: true,
          },
          {
            type: "dateTime",
            label: "Date de création",
            key: "createdAt",
            filters: ["gt", "lt"],
            compact: true,
          },
          {
            type: "dateTime",
            label: "Date de mise à jour",
            key: "updatedAt",
            filters: ["gt", "lt"],
          },
          {
            type: "text",
            label: "Statut",
            key: "status",
            filters: ["equals"],
            compact: true,
          },
        ],
      },
    },
  ];

  return { filterCategory, filters };
}

function stripEmptyOptionalFields(nodes: Record<string, any>) {
  const isEmptyValue = (val: any): boolean => {
    if (val === undefined || val === null || val === "") return true;
    if (typeof val === "string") return val === "{}" || val === "[]";
    if (typeof val === "object") return Object.keys(val).length === 0;
    return false;
  };
  Object.values(nodes || {}).forEach((node: any) => {
    if (isEmptyValue(node?.caseRelationSettings)) {
      delete node.caseRelationSettings;
    }
  });
}
function enrichProcessFromXmlNodes(processData: any, flowElements: any[]): any {
  const enrichedNodes: Record<string, any> = { ...processData.nodes };
  const links: any[] = processData.links || [];

  const firstActivityCode = links[0]?.target?.node || "";

  for (const flow of flowElements) {
    const id = flow.id || "";
    if (!id) continue;

    const existingNode = enrichedNodes[id] || {};
    const label = flow.name || existingNode.label || id;

    if (flow.$type === "bpmn:EndEvent") {
      enrichedNodes[id] = enrichEndNodeFromBpmnType(id, label, existingNode);
      continue;
    }

    if (flow.$type === "bpmn:StartEvent") {
      enrichedNodes[id] = {
        id,
        code: id,
        type: "startEvent",
        label,
        position: existingNode.position,
      };
      continue;
    }

    if (flow.$type === "bpmn:SequenceFlow") continue;

    const isFirstActivity = id === firstActivityCode;
    const enriched = enrichNodeFromBpmnType(
      id,
      flow.$type,
      label,
      isFirstActivity,
      existingNode,
    );
    enrichedNodes[id] = enriched;
  }

  const processCode = processData.code || "process";

  const hasRealForms = (formsObj: any): boolean => {
    if (!formsObj || Object.keys(formsObj).length === 0) return false;
    return Object.values(formsObj).some((f: any) => {
      const props = f?.schema?.properties || {};
      const keys = Object.keys(props);
      if (keys.length === 0) return false;
      if (keys.length === 1 && keys[0] === "commentaire") return false;
      return true;
    });
  };

  const forms = hasRealForms(processData.forms)
    ? processData.forms
    : processData.forms && Object.keys(processData.forms).length > 0
      ? { ...buildDefaultForms(enrichedNodes), ...processData.forms }
      : buildDefaultForms(enrichedNodes);

  const hasFilters =
    Array.isArray(processData.filters) && processData.filters.length > 0;
  const hasFilterCategory =
    Array.isArray(processData.filterCategory) &&
    processData.filterCategory.length > 0;

  const { filterCategory, filters } =
    hasFilters && hasFilterCategory
      ? {
          filterCategory: processData.filterCategory,
          filters: processData.filters,
        }
      : buildDefaultFiltersAndCategory(
          processCode,
          enrichedNodes,
          processData.label,
        );

  return {
    ...processData,
    nodes: enrichedNodes,
    forms,
    filterCategory,
    filters,
    actions:
      processData.actions && Object.keys(processData.actions).length > 0
        ? processData.actions
        : {
            owner: { comment: false },
            createdBy: { comment: false, "self-assign": true },
            roles: {
              R_READ_ALL: { comment: false },
              R_WRITE_ALL: { comment: false },
            },
            groups: {
              R_ACTIVITIES: { comment: false, "self-assign": true },
              GROUP_ADMIN: { comment: true },
            },
          },
    activityActions:
      processData.activityActions &&
      Object.keys(processData.activityActions).length > 0
        ? processData.activityActions
        : {
            owner: { comment: false },
            group: { comment: false },
            caseOwner: { comment: false },
            caseCreatedBy: { comment: false },
            roles: {
              R_READ_ALL: { comment: false },
              R_WRITE_ALL: { comment: false },
            },
            groups: {
              R_ACTIVITIES: { comment: false },
              GROUP_ADMIN: { comment: true },
            },
          },
  };
}

function fieldsToJsonSchema(fields: FormField[], required?: string[]) {
  const properties: Record<string, any> = {};
  const requiredFields = required || [];
  fields.forEach((field) => {
    if (field.type === "separator") return;
    let prop: any = {
      type:
        field.type === "textarea"
          ? "string"
          : field.type === "number"
            ? "number"
            : field.type === "boolean"
              ? "boolean"
              : field.type === "multiselect"
                ? "array"
                : "string",
      title: field.label,
    };
    if (field.helpText) prop.description = field.helpText;
    if (field.defaultValue) prop.default = field.defaultValue;
    if (["select", "multiselect", "radio"].includes(field.type)) {
      prop.enum = (field.options || []).map((opt) => opt.value);
    }
    properties[field.code] = prop;
    if (field.required && !requiredFields.includes(field.code)) {
      requiredFields.push(field.code);
    }
  });
  return {
    type: "object",
    properties,
    required: requiredFields.length > 0 ? requiredFields : undefined,
  };
}

function chunkByColumns(
  fieldsInGroup: FormField[],
  columns: number,
): FormField[][] {
  const rows: FormField[][] = [];
  let current: FormField[] = [];
  let used = 0;
  for (const f of fieldsInGroup) {
    const span = Math.min(f.colSpan || 1, columns);
    if (used + span > columns && current.length > 0) {
      rows.push(current);
      current = [];
      used = 0;
    }
    current.push(f);
    used += span;
  }
  if (current.length) rows.push(current);
  return rows;
}

function fieldToControl(field: FormField, columns: number): any {
  const span = Math.min(field.colSpan || 1, columns);
  const mdWidth = Math.round((12 / columns) * span);
  const control: any = {
    type: "Control",
    label: field.label,
    scope: `#/properties/${field.code}`,
    options: { grid: { md: mdWidth, sm: mdWidth, xs: 12 } },
  };
  if (["select", "radio"].includes(field.type)) {
    control.options.format = field.type === "radio" ? "radio" : "dropdown";
  }
  if (field.readOnly) control.options.readOnly = true;
  if (field.placeholder) control.options.placeholder = field.placeholder;
  if (field.conditionField && field.conditionValue) {
    control.rule = {
      effect: "SHOW",
      condition: {
        scope: `#/properties/${field.conditionField}`,
        schema: { const: field.conditionValue },
      },
    };
  }
  if (field.type === "apiAutocomplete") {
    control.options.component = "autocompleteLinkedField";
    if (field.freeSolo) control.options.freeSolo = true;
    control.options.params = {
      requestTemplate: {
        method: field.apiMethod || "GET",
        url: field.apiUrl || "",
        params: Object.fromEntries(
          (field.apiParams || []).map((p) => [p.key, p.value]),
        ),
        ...(field.apiHeaders && field.apiHeaders.length > 0
          ? {
              headers: Object.fromEntries(
                field.apiHeaders.map((h) => [h.key, h.value]),
              ),
            }
          : {}),
      },
      linkedFields: [],
      output: {
        items: field.itemsPath || "{{data.body}}",
        label: field.labelTemplate || `{{${field.code}}}`,
        value: field.valueTemplate || `{{${field.code}}}`,
      },
    };
    control.options.linkFields = { linkedFields: field.linkedFieldCodes || [] };
  }
  return control;
}

function buildLayoutUiSchema(
  fields: FormField[],
  layout: { sections: LayoutSection[] },
): any {
  const visibleFields = fields.filter(
    (f) => !f.hidden && f.type !== "separator",
  );
  const usedIds = new Set<string>();
  const categories: any[] = [];

  for (const section of layout.sections) {
    const groupElements: any[] = [];
    for (const group of section.groups) {
      const fieldsInGroup = visibleFields.filter(
        (f) => f.sectionId === section.id && f.groupId === group.id,
      );
      fieldsInGroup.forEach((f) => usedIds.add(f.id));
      const rows = chunkByColumns(fieldsInGroup, group.columns);
      groupElements.push({
        type: "Group",
        label: group.label,
        options: { variant: group.variant, groupId: group.id },
        elements: rows.map((row) => ({
          type: "HorizontalLayout",
          elements: row.map((f) => fieldToControl(f, group.columns)),
        })),
      });
    }
    categories.push({
      type: "Category",
      label: section.label,
      options: { sectionId: section.id },
      elements: groupElements,
    });
  }

  const unassigned = visibleFields.filter((f) => !usedIds.has(f.id));
  if (unassigned.length > 0) {
    const rows = chunkByColumns(unassigned, 2);
    categories.unshift({
      type: "Category",
      label: "Général",
      elements: [
        {
          type: "Group",
          options: { variant: "paper" },
          elements: rows.map((row) => ({
            type: "HorizontalLayout",
            elements: row.map((f) => fieldToControl(f, 2)),
          })),
        },
      ],
    });
  }

  return { type: "Categorization", elements: categories };
}

function fieldsToUiSchema(
  fields: FormField[],
  formLabel?: string,
  layout?: { sections: LayoutSection[] },
) {
  if (layout && layout.sections && layout.sections.length > 0) {
    return buildLayoutUiSchema(fields, layout);
  }

  const visibleFields = fields.filter((f) => !f.hidden);
  if (visibleFields.length === 0) {
    return { type: "Categorization", elements: [] };
  }
  const rows: FormField[][] = [];
  for (let i = 0; i < visibleFields.length; i += 2) {
    rows.push(visibleFields.slice(i, i + 2));
  }
  const categoryElements = rows.map((row) => ({
    type: "HorizontalLayout",
    elements: row.map((f) => fieldToControl(f, row.length === 1 ? 1 : 2)),
  }));
  return {
    type: "Categorization",
    elements: [
      {
        type: "Category",
        label: formLabel || "Formulaire",
        elements: [
          {
            type: "Group",
            options: { variant: "paper" },
            elements: categoryElements,
          },
        ],
      },
    ],
  };
}
function findControlByScope(uischema: any, scope: string): any | null {
  if (!uischema) return null;
  if (uischema.type === "Control" && uischema.scope === scope) return uischema;
  if (Array.isArray(uischema.elements)) {
    for (const el of uischema.elements) {
      const found = findControlByScope(el, scope);
      if (found) return found;
    }
  }
  return null;
}
function extractLayoutFromUiSchema(uischema: any): {
  sections: LayoutSection[];
  fieldMeta: Record<
    string,
    { sectionId: string; groupId: string; colSpan: number }
  >;
} {
  const sections: LayoutSection[] = [];
  const fieldMeta: Record<string, any> = {};
  if (
    !uischema ||
    uischema.type !== "Categorization" ||
    !Array.isArray(uischema.elements)
  ) {
    return { sections, fieldMeta };
  }
  uischema.elements.forEach((cat: any, catIdx: number) => {
    if (cat.type !== "Category") return;
    const isAutoGeneral = catIdx === 0 && cat.label === "Général";
    const sectionId = cat.options?.sectionId || `sec_${catIdx}`;
    const groups: LayoutGroup[] = [];
    (cat.elements || []).forEach((grp: any, grpIdx: number) => {
      if (grp.type !== "Group") return;
      const groupId = grp.options?.groupId || `grp_${catIdx}_${grpIdx}`;
      const variant: LayoutGroup["variant"] = grp.options?.variant || "paper";
      let columns = 1;
      (grp.elements || []).forEach((row: any) => {
        if (row.type === "HorizontalLayout") {
          columns = Math.max(columns, Math.min(4, (row.elements || []).length));
        }
      });
      if (!isAutoGeneral)
        groups.push({
          id: groupId,
          label: grp.label || "",
          variant,
          columns: columns as any,
        });
      (grp.elements || []).forEach((row: any) => {
        if (row.type !== "HorizontalLayout") return;
        const rowLen = (row.elements || []).length || 1;
        (row.elements || []).forEach((ctrl: any) => {
          if (ctrl.type !== "Control" || !ctrl.scope) return;
          const code = ctrl.scope.replace("#/properties/", "");
          const md = ctrl.options?.grid?.md ?? Math.round(12 / rowLen);
          const colSpan = Math.max(1, Math.round((md / 12) * columns));
          if (!isAutoGeneral) fieldMeta[code] = { sectionId, groupId, colSpan };
        });
      });
    });
    if (!isAutoGeneral)
      sections.push({
        id: sectionId,
        label: cat.label || `Section ${catIdx + 1}`,
        groups,
      });
  });
  return { sections, fieldMeta };
}
function schemaToFields(schema: any, uischema?: any): FormField[] {
  const fields: FormField[] = [];
  const requiredFields = schema?.required || [];
  const properties = schema?.properties || {};
  const { fieldMeta } = extractLayoutFromUiSchema(uischema);

  Object.entries(properties).forEach(([code, prop]: [string, any]) => {
    const control = findControlByScope(uischema, `#/properties/${code}`);
    const isApiAutocomplete =
      control?.options?.component === "autocompleteLinkedField";
    const fieldType: FieldType = isApiAutocomplete
      ? "apiAutocomplete"
      : prop.type === "number"
        ? "number"
        : prop.type === "boolean"
          ? "boolean"
          : prop.type === "array"
            ? "multiselect"
            : prop.enum
              ? "select"
              : "text";

    const field: FormField = {
      id: code,
      type: fieldType,
      code,
      label: prop.title || code,
      helpText: prop.description || "",
      defaultValue: prop.default || "",
      required: requiredFields.includes(code),
      readOnly: false,
      hidden: false,
      options: prop.enum
        ? prop.enum.map((v: string) => ({ label: v, value: v }))
        : undefined,
      ...(fieldMeta[code] || {}),
    };

    if (isApiAutocomplete) {
      const params = control.options.params || {};
      const rt = params.requestTemplate || {};
      field.apiMethod = (rt.method as "GET" | "POST") || "GET";
      field.apiUrl = rt.url || "";
      field.apiParams = Object.entries(rt.params || {}).map(([key, value]) => ({
        key,
        value: String(value),
      }));
      field.apiHeaders = rt.headers
        ? Object.entries(rt.headers).map(([key, value]) => ({
            key,
            value: String(value),
          }))
        : [];
      field.itemsPath = params.output?.items || "";
      field.labelTemplate = params.output?.label || "";
      field.valueTemplate = params.output?.value || "";
      field.freeSolo = !!control.options.freeSolo;
      field.linkedFieldCodes = control.options.linkFields?.linkedFields || [];
    }
    fields.push(field);
  });
  return fields;
}

function buildNumberControlOptions(
  gridMd: number,
  gridSm: number,
  gridXs: number = 12,
) {
  return {
    component: "NumberInput",
    options: {
      max: 999999999999,
      min: -999999999999,
      grid: { md: gridMd, sm: gridSm, xs: gridXs },
      step: 1,
      local: "fr-FR",
      format: "decimal",
      decimalPlaces: 2,
      decimalSeparator: ".",
      thousandSeparator: ",",
    },
  };
}
function validateNodesForSecurity(nodes: Record<string, any>): string | null {
  for (const [code, node] of Object.entries(nodes || {})) {
    const service = node?.service || "";
    const params = node?.params || {};
    const paramsStr =
      typeof params === "string" ? params : JSON.stringify(params);
    if (service === "JsNode") {
      const script = params?.script || paramsStr;
      const dangerous = [
        "process.",
        "require(",
        "__dirname",
        "__filename",
        "child_process",
        "eval(",
        "Function(",
        "global.",
        "Buffer.",
        "process.env",
        "process.exit",
        "fs.",
        "path.",
        "os.",
        "net.",
        "http.",
        "https.",
        "setTimeout",
        "setInterval",
        "setImmediate",
      ];
      for (const pattern of dangerous) {
        if (String(script).includes(pattern)) {
          return `Nœud "${code}" (JsNode) contient un pattern dangereux : "${pattern}"`;
        }
      }
    }
    if (service === "SQLNode") {
      const sql = (params?.sql || "").trim().toUpperCase();
      if (sql && !sql.startsWith("SELECT")) {
        return `Nœud "${code}" (SQLNode) : seules les requêtes SELECT sont autorisées`;
      }
      const values =
        typeof params?.values === "string"
          ? params.values
          : JSON.stringify(params?.values || "");
      const sqlInjections = [
        "--",
        "';",
        '";',
        "UNION ",
        "DROP ",
        "DELETE ",
        "TRUNCATE ",
        "INSERT ",
        "UPDATE ",
      ];
      for (const pattern of sqlInjections) {
        if (values.toUpperCase().includes(pattern)) {
          return `Nœud "${code}" (SQLNode) : injection SQL détectée dans les valeurs`;
        }
      }
    }
    if (service === "RestNode") {
      const requestTemplate = params?.requestTemplate || {};
      const url = String(requestTemplate?.url || "").toLowerCase();
      if (url) {
        const forbiddenHosts = [
          "localhost",
          "127.0.0.1",
          "0.0.0.0",
          "::1",
          "169.254.",
          "10.",
          "192.168.",
          "172.16.",
          "172.17.",
          "172.18.",
          "172.19.",
          "172.20.",
          "172.21.",
          "172.22.",
          "172.23.",
          "172.24.",
          "172.25.",
          "172.26.",
          "172.27.",
          "172.28.",
          "172.29.",
          "172.30.",
          "172.31.",
          "metadata.google",
          "metadata.aws",
        ];
        for (const host of forbiddenHosts) {
          if (url.includes(host)) {
            return `Nœud "${code}" (RestNode) : URL interne interdite (${host})`;
          }
        }
        const method = String(requestTemplate?.method || "").toUpperCase();
        const allowedMethods = ["GET", "POST", "PUT", "PATCH", "DELETE"];
        if (method && !allowedMethods.includes(method)) {
          return `Nœud "${code}" (RestNode) : méthode HTTP non autorisée : ${method}`;
        }
      }
    }
  }
  return null;
}

function applyReadonlyToPanel() {
  if (document.getElementById(READONLY_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = READONLY_STYLE_ID;
  style.innerHTML = `
    #js-properties-panel input,
    #js-properties-panel textarea,
    #js-properties-panel select {
      pointer-events: none !important;
      opacity: 0.65 !important;
      cursor: not-allowed !important;
      background-color: #f5f5f5 !important;
      color: #666 !important;
      user-select: text !important;
    }
    #js-properties-panel button,
    #js-properties-panel [role="button"],
    #js-properties-panel .bio-properties-panel-add-entry,
    #js-properties-panel .bio-properties-panel-remove-entry,
    #js-properties-panel [data-action],
    #js-properties-panel .cmd-add-element,
    #js-properties-panel .cmd-remove-element {
      pointer-events: none !important;
      opacity: 0.4 !important;
      cursor: not-allowed !important;
    }
    #js-properties-panel-readonly-banner {
      display: flex !important;
    }
  `;
  document.head.appendChild(style);
}

function removeReadonlyFromPanel() {
  document.getElementById(READONLY_STYLE_ID)?.remove();
  const banner = document.getElementById("js-properties-panel-readonly-banner");
  if (banner) banner.style.display = "none";
}

const diagramXML = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn2:definitions xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:bpmn2="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" id="Definitions_1" targetNamespace="http://bpmn.io/schema/bpmn" exporter="Camunda Modeler" exporterVersion="4.1.0" xsi:schemaLocation="http://www.omg.org/spec/BPMN/20100524/MODEL BPMN20.xsd">
  <bpmn2:process id="Process_1" isExecutable="false">
  </bpmn2:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1">
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn2:definitions>`;

var filename = "diagram";

function formatJson(value: any): any {
  if (!value) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
}

function stringify(value: any): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}

function tryParseJson(value: any): any {
  if (!value) return value;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function safeParseFormsField(value: any): Record<string, any> {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch {}
  }
  return {};
}

function cleanAndValidateXml(raw: string): string | null {
  let xml = raw
    .replace(/```xml\s*/gi, "")
    .replace(/```\s*/g, "")
    .trim();
  const xmlStart = xml.indexOf("<?xml");
  const defStart = xml.indexOf("<bpmn2:definitions");
  const startIdx = xmlStart !== -1 ? xmlStart : defStart;
  if (startIdx === -1) return null;
  xml = xml.substring(startIdx);
  const closeTag = "</bpmn2:definitions>";
  const endIndex = xml.indexOf(closeTag);
  if (endIndex !== -1) xml = xml.substring(0, endIndex + closeTag.length);
  return xml.trim();
}

function resolveNodeCode(ref: any): string {
  if (!ref) return "";
  return (
    ref?.businessObject?.code ||
    ref?.businessObject?.id ||
    ref?.code ||
    ref?.id ||
    ""
  );
}

function findProcessElement(rootElements: any[]): any | null {
  if (!rootElements?.length) return null;
  return rootElements.find((el: any) => el.$type === "bpmn:Process") || null;
}

const USER_API_URL = process.env.REACT_APP_USER_API_URL;

async function callBackendAi(
  prompt: string,
  maxTokens = 4000,
): Promise<string> {
  const token = sessionStorage.getItem("WFE_AUTH_TOKEN");
  if (!token)
    throw new Error("Session expirée — veuillez recharger l'éditeur.");
  const res = await fetch(`${USER_API_URL}/ai-proxy/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ prompt, maxTokens }),
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData?.message || `Erreur serveur IA (${res.status})`);
  }
  const data = await res.json();
  return data.text || "";
}

type FieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "boolean"
  | "select"
  | "multiselect"
  | "radio"
  | "file"
  | "user"
  | "separator"
  | "apiAutocomplete";

interface SelectOption {
  label: string;
  value: string;
}
interface LayoutGroup {
  id: string;
  label: string;
  variant: "paper" | "accordion" | "collapse";
  columns: 1 | 2 | 3 | 4;
}
interface LayoutSection {
  id: string;
  label: string;
  groups: LayoutGroup[];
}
interface FormField {
  id: string;
  type: FieldType;
  code: string;
  label: string;
  placeholder?: string;
  defaultValue?: string;
  required?: boolean;
  readOnly?: boolean;
  hidden?: boolean;
  helpText?: string;
  options?: SelectOption[];
  min?: number;
  max?: number;
  pattern?: string;
  conditionField?: string;
  conditionValue?: string;
  apiMethod?: "GET" | "POST";
  apiUrl?: string;
  apiParams?: { key: string; value: string }[];
  apiHeaders?: { key: string; value: string }[];
  itemsPath?: string;
  labelTemplate?: string;
  valueTemplate?: string;
  freeSolo?: boolean;
  linkedFieldCodes?: string[];
  sectionId?: string;
  groupId?: string;
  colSpan?: number;
}

interface DynamicFormSchemaLocal {
  id: string;
  label: string;
  fields: FormField[];
  updatedAt: string;
  code?: string;
  schema?: any;
  uischema?: any;
  scripts?: any[];
  transformers?: any[];
  layout?: { sections: LayoutSection[] };
  [key: string]: any;
}

interface FormManagerApi {
  forms: Record<string, DynamicFormSchemaLocal>;
  isOpen: boolean;
  editingFormId: string | null;
  formList: DynamicFormSchemaLocal[];
  openManager: (editId?: string) => void;
  closeManager: () => void;
  createForm: () => void;
  deleteForm: (id: string) => void;
  saveForm: (schema: DynamicFormSchemaLocal) => void;
  setEditingId: (id: string | null) => void;
  getFormById: (id: string) => DynamicFormSchemaLocal | null;
  getFormsAsJson: () => string;
}

const FIELD_TYPES_LOCAL: { type: FieldType; label: string }[] = [
  { type: "text", label: "Texte court" },
  { type: "textarea", label: "Texte long" },
  { type: "number", label: "Nombre" },
  { type: "date", label: "Date" },
  { type: "boolean", label: "Booléen" },
  { type: "select", label: "Liste déroulante" },
  { type: "multiselect", label: "Cases à cocher" },
  { type: "radio", label: "Boutons radio" },
  { type: "file", label: "Fichier" },
  { type: "user", label: "Utilisateur" },
  { type: "separator", label: "Séparateur" },
  { type: "apiAutocomplete", label: "Autocomplete (API)" },
];

function uid(): string {
  return Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 5);
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

// Fields referenced in an apiAutocomplete field's linkedFieldCodes are
// auto-filled by the search result and never rendered as their own Control
// in the uischema. They legitimately have no section/group placement — this
// lets the Disposition/Preview tabs distinguish them from fields that are
// simply forgotten.
function computeLinkedTargetCodes(fields: FormField[]): Set<string> {
  const set = new Set<string>();
  fields.forEach((f) => {
    if (f.type === "apiAutocomplete" && f.linkedFieldCodes) {
      f.linkedFieldCodes.forEach((c) => set.add(c));
    }
  });
  return set;
}

const FormManagerModalEnhanced: React.FC<{
  manager: FormManagerApi;
}> = ({ manager }) => {
  const { isOpen, formList, editingFormId, setEditingId } = manager;
  const [jsonViewId, setJsonViewId] = useState<string | null>(null);
  const [jsonViewText, setJsonViewText] = useState<string>("");
  const [jsonViewError, setJsonViewError] = useState<string | null>(null);

  const openJsonView = (id: string) => {
    const s = manager.getFormById(id);
    if (s) {
      setJsonViewText(JSON.stringify(s, null, 2));
      setJsonViewError(null);
      setJsonViewId(id);
    }
  };
  const closeJsonView = () => {
    setJsonViewId(null);
    setJsonViewText("");
    setJsonViewError(null);
  };
  const saveJsonView = () => {
    try {
      const parsed = JSON.parse(jsonViewText) as DynamicFormSchemaLocal;
      if (!parsed.id || !Array.isArray(parsed.fields))
        throw new Error("Champ 'id' ou 'fields' manquant");
      manager.saveForm({ ...parsed, updatedAt: new Date().toISOString() });
      closeJsonView();
    } catch (e: any) {
      setJsonViewError("JSON invalide : " + e.message);
    }
  };

  const [localSchema, setLocalSchema] = useState<DynamicFormSchemaLocal | null>(
    null,
  );
  const [tab, setTab] = useState<
    "list" | "builder" | "layout" | "preview" | "json"
  >("list");
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [jsonEditorText, setJsonEditorText] = useState<string>("");
  const [jsonEditorError, setJsonEditorError] = useState<string | null>(null);
  const dragSrcIdx = useRef<number | null>(null);
  const dragFieldRef = useRef<string | null>(null);
  const [previewActiveTab, setPreviewActiveTab] = useState<string>("");
  const [previewOpenGroups, setPreviewOpenGroups] = useState<
    Record<string, boolean>
  >({});

  const currentFormSnapshot = editingFormId
    ? JSON.stringify(manager.getFormById(editingFormId))
    : null;

  useEffect(() => {
    if (editingFormId) {
      const s = manager.getFormById(editingFormId);
      if (s) {
        const copy = JSON.parse(JSON.stringify(s)) as DynamicFormSchemaLocal;
        if (
          (!copy.fields || copy.fields.length === 0) &&
          copy.schema &&
          copy.uischema
        ) {
          copy.fields = schemaToFields(copy.schema, copy.uischema);
        }
        if (!copy.layout && copy.uischema) {
          const { sections } = extractLayoutFromUiSchema(copy.uischema);
          if (sections.length > 0) copy.layout = { sections };
        }
        setLocalSchema(copy);
        setJsonEditorText(JSON.stringify(copy, null, 2));
        setTab("builder");
        setSelectedFieldId(null);
        setJsonEditorError(null);
      }
    } else {
      setLocalSchema(null);
      setTab("list");
    }
  }, [editingFormId, currentFormSnapshot]);

  useEffect(() => {
    if (tab === "json" && localSchema) {
      setJsonEditorText(JSON.stringify(localSchema, null, 2));
      setJsonEditorError(null);
    }
  }, [tab]);

  const applyJsonToBuilder = () => {
    try {
      const parsed = JSON.parse(jsonEditorText) as DynamicFormSchemaLocal;
      if (!parsed.fields) throw new Error("Champ 'fields' manquant");
      setLocalSchema(parsed);
      setJsonEditorError(null);
      setTab("builder");
    } catch (e: any) {
      setJsonEditorError("JSON invalide : " + e.message);
    }
  };

  const selectedField =
    localSchema?.fields.find((f) => f.id === selectedFieldId) ?? null;

  const addField = (type: FieldType) => {
    const def = FIELD_TYPES_LOCAL.find((t) => t.type === type)!;
    const f: FormField = {
      id: uid(),
      type,
      code: slugify(def.label) + "_" + uid().slice(0, 4),
      label: def.label,
      required: false,
      readOnly: false,
      hidden: false,
      options: ["select", "multiselect", "radio"].includes(type)
        ? [
            { label: "Option 1", value: "option_1" },
            { label: "Option 2", value: "option_2" },
          ]
        : undefined,
      ...(type === "apiAutocomplete"
        ? {
            apiMethod: "GET" as const,
            apiUrl: "",
            apiParams: [],
            apiHeaders: [],
            itemsPath: "{{data.body}}",
            labelTemplate: "",
            valueTemplate: "",
            freeSolo: false,
            linkedFieldCodes: [],
          }
        : {}),
    };
    setLocalSchema((p) => (p ? { ...p, fields: [...p.fields, f] } : p));
    setSelectedFieldId(f.id);
    if (tab === "json" && localSchema) {
      const next = {
        ...localSchema,
        fields: [...(localSchema.fields || []), f],
      };
      setJsonEditorText(JSON.stringify(next, null, 2));
    }
  };

  const removeField = (id: string) => {
    setLocalSchema((p) =>
      p ? { ...p, fields: p.fields.filter((f) => f.id !== id) } : p,
    );
    setSelectedFieldId((s) => (s === id ? null : s));
  };
  const addSection = () => {
    const s: LayoutSection = { id: uid(), label: "Nouvel onglet", groups: [] };
    setLocalSchema((p) =>
      p
        ? { ...p, layout: { sections: [...(p.layout?.sections || []), s] } }
        : p,
    );
  };
  const removeSection = (sectionId: string) => {
    setLocalSchema((p) =>
      p
        ? {
            ...p,
            layout: {
              sections: (p.layout?.sections || []).filter(
                (s) => s.id !== sectionId,
              ),
            },
          }
        : p,
    );
  };
  const updateSection = (sectionId: string, patch: Partial<LayoutSection>) => {
    setLocalSchema((p) =>
      p
        ? {
            ...p,
            layout: {
              sections: (p.layout?.sections || []).map((s) =>
                s.id === sectionId ? { ...s, ...patch } : s,
              ),
            },
          }
        : p,
    );
  };
  const addGroup = (sectionId: string) => {
    const g: LayoutGroup = {
      id: uid(),
      label: "Nouveau groupe",
      variant: "paper",
      columns: 2,
    };
    setLocalSchema((p) =>
      p
        ? {
            ...p,
            layout: {
              sections: (p.layout?.sections || []).map((s) =>
                s.id === sectionId ? { ...s, groups: [...s.groups, g] } : s,
              ),
            },
          }
        : p,
    );
  };
  const removeGroup = (sectionId: string, groupId: string) => {
    setLocalSchema((p) =>
      p
        ? {
            ...p,
            layout: {
              sections: (p.layout?.sections || []).map((s) =>
                s.id === sectionId
                  ? { ...s, groups: s.groups.filter((g) => g.id !== groupId) }
                  : s,
              ),
            },
          }
        : p,
    );
  };
  const updateGroup = (
    sectionId: string,
    groupId: string,
    patch: Partial<LayoutGroup>,
  ) => {
    setLocalSchema((p) =>
      p
        ? {
            ...p,
            layout: {
              sections: (p.layout?.sections || []).map((s) =>
                s.id === sectionId
                  ? {
                      ...s,
                      groups: s.groups.map((g) =>
                        g.id === groupId ? { ...g, ...patch } : g,
                      ),
                    }
                  : s,
              ),
            },
          }
        : p,
    );
  };
  const duplicateField = (id: string) => {
    setLocalSchema((p) => {
      if (!p) return p;
      const i = p.fields.findIndex((f) => f.id === id);
      if (i === -1) return p;
      const copy: FormField = {
        ...p.fields[i],
        id: uid(),
        label: p.fields[i].label + " (copie)",
        code: p.fields[i].code + "_copy",
      };
      const fields = [...p.fields];
      fields.splice(i + 1, 0, copy);
      return { ...p, fields };
    });
  };

  const updateField = (id: string, patch: Partial<FormField>) =>
    setLocalSchema((p) =>
      p
        ? {
            ...p,
            fields: p.fields.map((f) => (f.id === id ? { ...f, ...patch } : f)),
          }
        : p,
    );

  const moveField = (from: number, to: number) =>
    setLocalSchema((p) => {
      if (!p) return p;
      const fields = [...p.fields];
      const [m] = fields.splice(from, 1);
      fields.splice(to, 0, m);
      return { ...p, fields };
    });

  // Repositions fieldId in the global fields array, just before beforeFieldId
  // (or at the end when beforeFieldId is null/omitted). Used by drag & drop
  // in the "Disposition" tab so fields keep a stable order inside a group.
  const moveFieldToPosition = (
    fieldId: string,
    beforeFieldId?: string | null,
  ) => {
    setLocalSchema((p) => {
      if (!p) return p;
      const fields = [...p.fields];
      const fromIdx = fields.findIndex((f) => f.id === fieldId);
      if (fromIdx === -1) return p;
      const [moved] = fields.splice(fromIdx, 1);
      let toIdx = fields.length;
      if (beforeFieldId) {
        const idx = fields.findIndex((f) => f.id === beforeFieldId);
        if (idx !== -1) toIdx = idx;
      }
      fields.splice(toIdx, 0, moved);
      return { ...p, fields };
    });
  };

  // Assigns a field to a section/group (or clears both when passed undefined)
  // and repositions it right before targetFieldId when given.
  const assignFieldToGroup = (
    fieldId: string,
    sectionId: string | undefined,
    groupId: string | undefined,
    beforeFieldId?: string | null,
  ) => {
    setLocalSchema((p) => {
      if (!p) return p;
      const fields = p.fields.map((f) =>
        f.id === fieldId ? { ...f, sectionId, groupId } : f,
      );
      return { ...p, fields };
    });
    if (beforeFieldId !== undefined) {
      // Defer the reorder to the next tick so it applies on top of the
      // sectionId/groupId update above (setLocalSchema updates are batched
      // but we still want a clear, single source of truth for ordering).
      setTimeout(() => moveFieldToPosition(fieldId, beforeFieldId), 0);
    }
  };

  const buildPreviewTabs = (): {
    id: string;
    label: string;
    isVirtual?: boolean;
    fields: FormField[];
    groups: { group: LayoutGroup; fields: FormField[] }[];
  }[] => {
    const visibleFields = (localSchema?.fields || []).filter((f) => !f.hidden);
    const sections = localSchema?.layout?.sections || [];
    if (sections.length === 0) {
      return [
        {
          id: "__all__",
          label: localSchema?.label || "Formulaire",
          isVirtual: true,
          fields: visibleFields,
          groups: [],
        },
      ];
    }
    const sectionIds = new Set(sections.map((s) => s.id));
    const linkedTargetCodes = computeLinkedTargetCodes(visibleFields);
    const unassigned = visibleFields.filter(
      (f) =>
        (!f.sectionId || !sectionIds.has(f.sectionId)) &&
        !linkedTargetCodes.has(f.code),
    );
    const tabs: {
      id: string;
      label: string;
      isVirtual?: boolean;
      fields: FormField[];
      groups: { group: LayoutGroup; fields: FormField[] }[];
    }[] = [];
    if (unassigned.length > 0) {
      tabs.push({
        id: "__general__",
        label: "Général",
        isVirtual: true,
        fields: unassigned,
        groups: [],
      });
    }
    sections.forEach((section) => {
      const groupIds = new Set(section.groups.map((g) => g.id));
      const sectionFields = visibleFields.filter(
        (f) => f.sectionId === section.id,
      );
      const ungrouped = sectionFields.filter(
        (f) => !f.groupId || !groupIds.has(f.groupId),
      );
      const groups = section.groups.map((group) => ({
        group,
        fields: sectionFields.filter((f) => f.groupId === group.id),
      }));
      tabs.push({
        id: section.id,
        label: section.label,
        fields: ungrouped,
        groups,
      });
    });
    return tabs;
  };

  useEffect(() => {
    if (tab !== "preview") return;
    const tabs = buildPreviewTabs();
    if (!tabs.some((t) => t.id === previewActiveTab)) {
      setPreviewActiveTab(tabs[0]?.id || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, localSchema]);

  const renderPreviewField = (f: FormField) => {
    if (f.type === "separator") {
      return (
        <hr
          key={f.id}
          style={{ border: "none", borderTop: "0.5px solid #eee" }}
        />
      );
    }
    return (
      <div key={f.id}>
        <label
          style={{
            fontSize: "13px",
            fontWeight: 500,
            display: "block",
            marginBottom: "4px",
          }}
        >
          {f.label}
          {f.required && (
            <span style={{ color: "#E24B4A", marginLeft: "3px" }}>*</span>
          )}
        </label>
        {f.helpText && (
          <p style={{ fontSize: "11px", color: "#999", marginBottom: "4px" }}>
            {f.helpText}
          </p>
        )}
        {f.type === "textarea" ? (
          <textarea
            style={{
              width: "100%",
              padding: "7px 10px",
              border: "0.5px solid #ccc",
              borderRadius: "6px",
              fontSize: "13px",
              background: "#f5f5f5",
              minHeight: "60px",
              boxSizing: "border-box",
            }}
            placeholder={f.placeholder}
            readOnly={f.readOnly}
          />
        ) : f.type === "boolean" ? (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            <input type="checkbox" disabled={f.readOnly} /> {f.label}
          </label>
        ) : f.type === "select" || f.type === "apiAutocomplete" ? (
          <select
            style={{
              width: "100%",
              padding: "7px 10px",
              border: "0.5px solid #ccc",
              borderRadius: "6px",
              fontSize: "13px",
              background: "#f5f5f5",
            }}
            disabled={f.readOnly}
          >
            <option value="">— Sélectionner —</option>
            {(f.options || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : f.type === "multiselect" || f.type === "radio" ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {(f.options || []).length === 0 ? (
              <span style={{ fontSize: "12px", color: "#bbb" }}>
                (aucune option définie)
              </span>
            ) : (
              (f.options || []).map((o) => (
                <label
                  key={o.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "12px",
                    border: "0.5px solid #ddd",
                    borderRadius: "5px",
                    padding: "3px 8px",
                  }}
                >
                  <input
                    type={f.type === "radio" ? "radio" : "checkbox"}
                    name={f.code}
                    disabled={f.readOnly}
                  />
                  {o.label}
                </label>
              ))
            )}
          </div>
        ) : f.type === "date" ? (
          <input
            type="date"
            style={{
              width: "100%",
              padding: "7px 10px",
              border: "0.5px solid #ccc",
              borderRadius: "6px",
              fontSize: "13px",
              background: "#f5f5f5",
              boxSizing: "border-box",
            }}
            readOnly={f.readOnly}
          />
        ) : f.type === "file" ? (
          <input
            type="file"
            disabled={f.readOnly}
            style={{ fontSize: "12px" }}
          />
        ) : (
          <input
            type={f.type === "number" ? "number" : "text"}
            style={{
              width: "100%",
              padding: "7px 10px",
              border: "0.5px solid #ccc",
              borderRadius: "6px",
              fontSize: "13px",
              background: "#f5f5f5",
              boxSizing: "border-box",
            }}
            placeholder={f.placeholder}
            readOnly={f.readOnly}
          />
        )}
      </div>
    );
  };

  const handleSave = () => {
    if (!localSchema) return;
    let schemaToSave: DynamicFormSchemaLocal;
    if (tab === "json") {
      try {
        const parsed = JSON.parse(jsonEditorText) as DynamicFormSchemaLocal;
        schemaToSave = parsed;
      } catch {
        schemaToSave = localSchema;
      }
    } else if (tab === "builder" || tab === "layout") {
      const schema = fieldsToJsonSchema(localSchema.fields || []);
      const uischema = fieldsToUiSchema(
        localSchema.fields || [],
        localSchema.label,
        localSchema.layout,
      );
      const original = manager.getFormById(localSchema.id);
      schemaToSave = {
        ...original,
        ...localSchema,
        id: localSchema.id,
        label: localSchema.label,
        schema,
        uischema,
        fields: localSchema.fields,
        layout: localSchema.layout,
        updatedAt: new Date().toISOString(),
      };
    } else {
      schemaToSave = localSchema;
    }
    manager.saveForm(schemaToSave);
    manager.closeManager();
  };

  if (!isOpen) return null;

  const styleBox: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 99999,
  };
  const modalBox: React.CSSProperties = {
    width: "min(960px, 96vw)",
    height: "min(700px, 94vh)",
    background: "#fff",
    borderRadius: "12px",
    border: "0.5px solid #ccc",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    fontFamily: "system-ui, sans-serif",
    fontSize: "14px",
    color: "#222",
    boxShadow: "0 24px 56px rgba(0,0,0,0.2)",
  };
  const primaryBtnStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "7px 14px",
    fontSize: "13px",
    fontWeight: 500,
    border: "none",
    borderRadius: "6px",
    background: "#1565C0",
    color: "#fff",
    cursor: "pointer",
  };
  const secondaryBtnStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "6px 12px",
    fontSize: "13px",
    border: "0.5px solid #ccc",
    borderRadius: "6px",
    background: "#fff",
    color: "#222",
    cursor: "pointer",
  };
  const jsonBtnStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "5px 10px",
    fontSize: "12px",
    border: "0.5px solid #1565C0",
    borderRadius: "6px",
    background: "transparent",
    color: "#1565C0",
    cursor: "pointer",
    fontWeight: 500,
  };
  const inpSt: React.CSSProperties = {
    width: "100%",
    fontSize: "13px",
    padding: "5px 8px",
    border: "0.5px solid #ccc",
    borderRadius: "6px",
    background: "#f5f5f5",
    color: "#222",
    boxSizing: "border-box",
  };

  const TABS = editingFormId
    ? (["builder", "layout", "preview", "json"] as const)
    : [];

  return (
    <>
      {jsonViewId && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100001,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeJsonView();
          }}
        >
          <div
            style={{
              width: "min(700px,94vw)",
              height: "min(560px,90vh)",
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 24px 56px rgba(0,0,0,0.25)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              fontFamily: "system-ui,sans-serif",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "12px 16px",
                borderBottom: "1px solid #eee",
                background: "#f8fafd",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  flex: 1,
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#1565C0",
                }}
              >
                Vue JSON —{" "}
                {manager.getFormById(jsonViewId)?.label || jsonViewId}
              </span>
              <button
                onClick={saveJsonView}
                style={{
                  backgroundColor: "#1565C0",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 16px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Sauvegarder
              </button>
              <button
                onClick={closeJsonView}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "22px",
                  color: "#888",
                  padding: "2px 6px",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                padding: "12px 16px",
                gap: "8px",
              }}
            >
              {jsonViewError && (
                <div
                  style={{
                    padding: "8px 12px",
                    background: "#fce4ec",
                    color: "#c62828",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: 500,
                    border: "1px solid #ef9a9a",
                  }}
                >
                  {jsonViewError}
                </div>
              )}
              <textarea
                value={jsonViewText}
                onChange={(e) => {
                  setJsonViewText(e.target.value);
                  setJsonViewError(null);
                }}
                spellCheck={false}
                style={{
                  flex: 1,
                  width: "100%",
                  fontFamily: "'Courier New',Courier,monospace",
                  fontSize: "13px",
                  lineHeight: "1.6",
                  border: "1.5px solid #1565C0",
                  borderRadius: "8px",
                  padding: "12px",
                  resize: "none",
                  outline: "none",
                  color: "#1a237e",
                  background: "#f0f7ff",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div
        style={styleBox}
        onClick={(e) => {
          if (e.target === e.currentTarget) manager.closeManager();
        }}
      >
        <div style={modalBox}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "12px 16px",
              borderBottom: "0.5px solid #eee",
              flexShrink: 0,
              background: "#f8fafd",
            }}
          >
            {editingFormId && (
              <button
                onClick={() => {
                  setEditingId(null);
                  setTab("list");
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "18px",
                  color: "#666",
                  padding: "2px 6px",
                  borderRadius: "4px",
                }}
                title="Retour"
              >
                ←
              </button>
            )}
            <div style={{ flex: 1 }}>
              <p
                style={{
                  margin: 0,
                  fontWeight: 500,
                  fontSize: "15px",
                  color: "#1565C0",
                }}
              >
                {editingFormId
                  ? localSchema?.label || "Formulaire"
                  : "Gestionnaire de formulaires"}
              </p>
              {editingFormId && (
                <p style={{ margin: 0, fontSize: "11px", color: "#999" }}>
                  {localSchema?.fields.length ?? 0} champ
                  {(localSchema?.fields.length ?? 0) !== 1 ? "s" : ""}
                </p>
              )}
            </div>
            {editingFormId && (
              <div
                style={{
                  display: "flex",
                  border: "0.5px solid #ccc",
                  borderRadius: "7px",
                  overflow: "hidden",
                }}
              >
                {TABS.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t as any)}
                    disabled={
                      t === "preview" && (localSchema?.fields.length ?? 0) === 0
                    }
                    style={{
                      padding: "5px 14px",
                      fontSize: "12px",
                      border: "none",
                      cursor: "pointer",
                      background: tab === t ? "#1565C0" : "transparent",
                      color: tab === t ? "#fff" : "#666",
                      opacity:
                        t === "preview" &&
                        (localSchema?.fields.length ?? 0) === 0
                          ? 0.4
                          : 1,
                      fontWeight: tab === t ? 500 : 400,
                    }}
                  >
                    {t === "builder"
                      ? "Builder"
                      : t === "layout"
                        ? "Disposition"
                        : t === "preview"
                          ? "Aperçu"
                          : "JSON"}
                  </button>
                ))}
              </div>
            )}
            {editingFormId && (
              <button onClick={handleSave} style={primaryBtnStyle}>
                Sauvegarder
              </button>
            )}
            <button
              onClick={manager.closeManager}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "22px",
                color: "#888",
                padding: "2px 6px",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          {!editingFormId ? (
            <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: "14px",
                }}
              >
                <button onClick={manager.createForm} style={primaryBtnStyle}>
                  + Nouveau formulaire
                </button>
              </div>
              {formList.length === 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                    color: "#aaa",
                    fontSize: "14px",
                    padding: "60px 20px",
                  }}
                >
                  <span style={{ fontSize: "48px" }}>📋</span>
                  Aucun formulaire. Créez-en un pour commencer.
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(240px, 1fr))",
                    gap: "10px",
                  }}
                >
                  {formList.map((f: any) => (
                    <div
                      key={f.id}
                      style={{
                        background: "#fff",
                        border: "0.5px solid #eee",
                        borderRadius: "10px",
                        padding: "14px 16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        boxShadow: "0 2px 8px rgba(21,101,192,0.06)",
                      }}
                    >
                      <div>
                        <p
                          style={{
                            margin: 0,
                            fontWeight: 500,
                            fontSize: "14px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "#1565C0",
                          }}
                        >
                          {f.label}
                        </p>
                        <p
                          style={{ margin: 0, fontSize: "11px", color: "#999" }}
                        >
                          {f.fields?.length ?? 0} champ
                          {(f.fields?.length ?? 0) !== 1 ? "s" : ""} ·{" "}
                          {new Date(f.updatedAt).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "5px",
                          marginTop: "2px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          onClick={() => setEditingId(f.id)}
                          style={{
                            ...secondaryBtnStyle,
                            flex: 1,
                            justifyContent: "center",
                            fontSize: "12px",
                            padding: "5px 8px",
                          }}
                        >
                          ✎ Modifier
                        </button>
                        <button
                          onClick={() => openJsonView(f.id)}
                          style={{
                            ...jsonBtnStyle,
                            fontSize: "12px",
                            padding: "5px 8px",
                          }}
                        >
                          {"{ }"} JSON
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Supprimer "${f.label}" ?`))
                              manager.deleteForm(f.id);
                          }}
                          style={{
                            ...secondaryBtnStyle,
                            fontSize: "12px",
                            padding: "5px 8px",
                            color: "#E24B4A",
                            borderColor: "#F09595",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : tab === "json" ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                padding: "14px 16px",
                gap: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    color: "#666",
                    fontWeight: 500,
                  }}
                >
                  Éditez le JSON directement. Cliquez sur "Appliquer" pour
                  mettre à jour le builder.
                </p>
                <button
                  onClick={applyJsonToBuilder}
                  style={{
                    ...primaryBtnStyle,
                    fontSize: "12px",
                    padding: "5px 14px",
                    background: "#4CAF50",
                  }}
                >
                  ✓ Appliquer au builder
                </button>
              </div>
              {jsonEditorError && (
                <div
                  style={{
                    padding: "8px 12px",
                    background: "#fce4ec",
                    color: "#c62828",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: 500,
                    border: "1px solid #ef9a9a",
                  }}
                >
                  {jsonEditorError}
                </div>
              )}
              <textarea
                value={jsonEditorText}
                onChange={(e) => {
                  setJsonEditorText(e.target.value);
                  setJsonEditorError(null);
                }}
                spellCheck={false}
                style={{
                  flex: 1,
                  width: "100%",
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: "13px",
                  lineHeight: "1.6",
                  border: "1.5px solid #1565C0",
                  borderRadius: "8px",
                  padding: "12px",
                  resize: "none",
                  outline: "none",
                  color: "#1a237e",
                  background: "#f0f7ff",
                  boxSizing: "border-box",
                }}
              />
            </div>
          ) : tab === "layout" ? (
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "14px",
                alignContent: "flex-start",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <p style={{ margin: 0, fontSize: "13px", color: "#666" }}>
                  Organisez vos champs en onglets et groupes. Glissez-déposez un
                  champ pour l'affecter (ou retirez-le avec ✕).
                </p>
                <button onClick={addSection} style={primaryBtnStyle}>
                  + Onglet
                </button>
              </div>

              {(() => {
                const sections = localSchema?.layout?.sections || [];
                const sectionIds = new Set(sections.map((s) => s.id));
                const allFields = localSchema?.fields || [];
                const linkedTargetCodes = computeLinkedTargetCodes(allFields);
                const isUnplaced = (f: FormField) =>
                  f.type !== "separator" &&
                  (!f.sectionId || !sectionIds.has(f.sectionId));
                const unassignedFields = allFields.filter(
                  (f) => isUnplaced(f) && !linkedTargetCodes.has(f.code),
                );
                const autoLinkedFields = allFields.filter(
                  (f) => isUnplaced(f) && linkedTargetCodes.has(f.code),
                );

                const fieldChipStyle: React.CSSProperties = {
                  padding: "5px 10px",
                  border: "0.5px solid #ccc",
                  borderRadius: "6px",
                  background: "#fff",
                  fontSize: "12px",
                  cursor: "grab",
                  userSelect: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                };

                const removeBtnStyle: React.CSSProperties = {
                  cursor: "pointer",
                  color: "#E24B4A",
                  fontSize: "11px",
                  lineHeight: 1,
                };

                return (
                  <>
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        const draggedId = dragFieldRef.current;
                        if (draggedId)
                          assignFieldToGroup(draggedId, undefined, undefined);
                        dragFieldRef.current = null;
                      }}
                      style={{
                        position: "relative",
                        flexShrink: 0,
                        border: "1.5px dashed #ccc",
                        borderRadius: "10px",
                        padding: "10px 12px",
                        display: "flex",
                        flexWrap: "wrap",
                        alignContent: "flex-start",
                        gap: "6px",
                        minHeight: "40px",
                        alignItems: "center",
                        background: "#fafafa",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "11px",
                          color: "#999",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: ".03em",
                          marginRight: "4px",
                          flexShrink: 0,
                        }}
                      >
                        Non affectés
                      </span>
                      {unassignedFields.length === 0 ? (
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#bbb",
                            fontStyle: "italic",
                          }}
                        >
                          (déposez ici pour retirer un champ)
                        </span>
                      ) : (
                        unassignedFields.map((f) => (
                          <div
                            key={f.id}
                            draggable
                            onDragStart={() => {
                              dragFieldRef.current = f.id;
                            }}
                            onDragEnd={() => {
                              dragFieldRef.current = null;
                            }}
                            style={fieldChipStyle}
                            title={f.code}
                          >
                            {f.label}
                          </div>
                        ))
                      )}
                    </div>

                    {autoLinkedFields.length > 0 && (
                      <div
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          const draggedId = dragFieldRef.current;
                          if (draggedId)
                            assignFieldToGroup(draggedId, undefined, undefined);
                          dragFieldRef.current = null;
                        }}
                        style={{
                          position: "relative",
                          flexShrink: 0,
                          border: "1.5px dashed #d9c68a",
                          borderRadius: "10px",
                          padding: "10px 12px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          background: "#fdfbf3",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#9a7d1f",
                            fontWeight: 600,
                          }}
                        >
                          Champs auto-remplis (via une recherche API) — pas
                          besoin d'emplacement, sauf si vous voulez les afficher
                          manuellement
                        </span>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignContent: "flex-start",
                            gap: "6px",
                          }}
                        >
                          {autoLinkedFields.map((f) => (
                            <div
                              key={f.id}
                              draggable
                              onDragStart={() => {
                                dragFieldRef.current = f.id;
                              }}
                              onDragEnd={() => {
                                dragFieldRef.current = null;
                              }}
                              style={{
                                ...fieldChipStyle,
                                borderColor: "#d9c68a",
                                background: "#fff9e8",
                                color: "#7a6015",
                              }}
                              title={f.code}
                            >
                              {f.label}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {sections.length === 0 && (
                      <p
                        style={{
                          fontSize: "13px",
                          color: "#aaa",
                          fontStyle: "italic",
                          textAlign: "center",
                          padding: "30px",
                        }}
                      >
                        Aucune disposition définie — le formulaire utilise la
                        mise en page par défaut (2 colonnes, un seul groupe).
                      </p>
                    )}

                    {sections.map((section) => {
                      const groupIds = new Set(section.groups.map((g) => g.id));
                      const sectionFields = allFields.filter(
                        (f) => f.sectionId === section.id,
                      );
                      const sectionUngrouped = sectionFields.filter(
                        (f) => !f.groupId || !groupIds.has(f.groupId),
                      );

                      return (
                        <div
                          key={section.id}
                          style={{
                            position: "relative",
                            flexShrink: 0,
                            border: "0.5px solid #ddd",
                            borderRadius: "10px",
                            padding: "12px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                              flexShrink: 0,
                            }}
                          >
                            <input
                              value={section.label}
                              onChange={(e) =>
                                updateSection(section.id, {
                                  label: e.target.value,
                                })
                              }
                              style={{ ...inpSt, flex: 1, fontWeight: 600 }}
                              placeholder="Nom de l'onglet"
                            />
                            <button
                              onClick={() => addGroup(section.id)}
                              style={secondaryBtnStyle}
                            >
                              + Groupe
                            </button>
                            <button
                              onClick={() => removeSection(section.id)}
                              style={{
                                ...secondaryBtnStyle,
                                color: "#E24B4A",
                                borderColor: "#F09595",
                              }}
                            >
                              ✕ Onglet
                            </button>
                          </div>

                          <div
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => {
                              const draggedId = dragFieldRef.current;
                              if (draggedId)
                                assignFieldToGroup(
                                  draggedId,
                                  section.id,
                                  undefined,
                                );
                              dragFieldRef.current = null;
                            }}
                            style={{
                              flexShrink: 0,
                              border: "1px dashed #ddd",
                              borderRadius: "8px",
                              padding: "8px 10px",
                              display: "flex",
                              flexWrap: "wrap",
                              alignContent: "flex-start",
                              gap: "6px",
                              minHeight: "34px",
                              background: "#fcfcfc",
                            }}
                          >
                            {sectionUngrouped.length === 0 ? (
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "#bbb",
                                  fontStyle: "italic",
                                }}
                              >
                                Champs de cet onglet sans groupe (glissez ici)
                              </span>
                            ) : (
                              sectionUngrouped.map((f) => (
                                <div
                                  key={f.id}
                                  draggable
                                  onDragStart={() => {
                                    dragFieldRef.current = f.id;
                                  }}
                                  onDragEnd={() => {
                                    dragFieldRef.current = null;
                                  }}
                                  style={fieldChipStyle}
                                  title={f.code}
                                >
                                  {f.label}
                                  <span
                                    onClick={() =>
                                      assignFieldToGroup(
                                        f.id,
                                        undefined,
                                        undefined,
                                      )
                                    }
                                    style={removeBtnStyle}
                                    title="Retirer de l'onglet"
                                  >
                                    ✕
                                  </span>
                                </div>
                              ))
                            )}
                          </div>

                          {section.groups.map((group) => {
                            const groupFields = sectionFields.filter(
                              (f) => f.groupId === group.id,
                            );
                            return (
                              <div
                                key={group.id}
                                style={{
                                  position: "relative",
                                  flexShrink: 0,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "6px",
                                  paddingLeft: "16px",
                                  borderLeft: "3px solid #1565C0",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  <input
                                    value={group.label}
                                    onChange={(e) =>
                                      updateGroup(section.id, group.id, {
                                        label: e.target.value,
                                      })
                                    }
                                    style={{ ...inpSt, flex: 1 }}
                                    placeholder="Nom du groupe"
                                  />
                                  <select
                                    value={group.variant}
                                    onChange={(e) =>
                                      updateGroup(section.id, group.id, {
                                        variant: e.target
                                          .value as LayoutGroup["variant"],
                                      })
                                    }
                                    style={{ ...inpSt, width: "130px" }}
                                  >
                                    <option value="paper">Carte</option>
                                    <option value="accordion">Accordéon</option>
                                    <option value="collapse">Repliable</option>
                                  </select>
                                  <select
                                    value={group.columns}
                                    onChange={(e) =>
                                      updateGroup(section.id, group.id, {
                                        columns: Number(
                                          e.target.value,
                                        ) as LayoutGroup["columns"],
                                      })
                                    }
                                    style={{ ...inpSt, width: "110px" }}
                                  >
                                    <option value={1}>1 colonne</option>
                                    <option value={2}>2 colonnes</option>
                                    <option value={3}>3 colonnes</option>
                                    <option value={4}>4 colonnes</option>
                                  </select>
                                  <button
                                    onClick={() =>
                                      removeGroup(section.id, group.id)
                                    }
                                    style={{
                                      background: "none",
                                      border: "none",
                                      color: "#E24B4A",
                                      cursor: "pointer",
                                    }}
                                  >
                                    ✕
                                  </button>
                                </div>

                                <div
                                  onDragOver={(e) => e.preventDefault()}
                                  onDrop={() => {
                                    const draggedId = dragFieldRef.current;
                                    if (draggedId) {
                                      assignFieldToGroup(
                                        draggedId,
                                        section.id,
                                        group.id,
                                        null,
                                      );
                                    }
                                    dragFieldRef.current = null;
                                  }}
                                  style={{
                                    flexShrink: 0,
                                    border: "1px dashed #cfe0f7",
                                    borderRadius: "8px",
                                    padding: "8px 10px",
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignContent: "flex-start",
                                    gap: "6px",
                                    minHeight: "34px",
                                    background: "#f5f9ff",
                                  }}
                                >
                                  {groupFields.length === 0 ? (
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        color: "#9fbede",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      Glissez des champs ici
                                    </span>
                                  ) : (
                                    groupFields.map((f) => (
                                      <div
                                        key={f.id}
                                        draggable
                                        onDragStart={() => {
                                          dragFieldRef.current = f.id;
                                        }}
                                        onDragEnd={() => {
                                          dragFieldRef.current = null;
                                        }}
                                        onDragOver={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onDrop={(e) => {
                                          e.stopPropagation();
                                          const draggedId =
                                            dragFieldRef.current;
                                          if (draggedId && draggedId !== f.id) {
                                            assignFieldToGroup(
                                              draggedId,
                                              section.id,
                                              group.id,
                                              f.id,
                                            );
                                          }
                                          dragFieldRef.current = null;
                                        }}
                                        style={{
                                          ...fieldChipStyle,
                                          borderColor: "#1565C0",
                                        }}
                                        title={f.code}
                                      >
                                        {f.label}
                                        <span
                                          onClick={() =>
                                            assignFieldToGroup(
                                              f.id,
                                              undefined,
                                              undefined,
                                            )
                                          }
                                          style={removeBtnStyle}
                                          title="Retirer du groupe"
                                        >
                                          ✕
                                        </span>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          ) : tab === "preview" ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              {(() => {
                const previewTabs = buildPreviewTabs();
                const showTabBar =
                  previewTabs.length > 1 ||
                  (previewTabs.length === 1 && !previewTabs[0].isVirtual);
                const active =
                  previewTabs.find((t) => t.id === previewActiveTab) ||
                  previewTabs[0];
                const isEmpty =
                  !active ||
                  (active.fields.length === 0 &&
                    active.groups.every((g) => g.fields.length === 0));

                return (
                  <>
                    {showTabBar && (
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          padding: "10px 16px 0",
                          borderBottom: "0.5px solid #eee",
                          flexWrap: "wrap",
                          flexShrink: 0,
                        }}
                      >
                        {previewTabs.map((t) => (
                          <button
                            key={t.id}
                            onClick={() => setPreviewActiveTab(t.id)}
                            style={{
                              padding: "8px 16px",
                              fontSize: "13px",
                              fontWeight: active?.id === t.id ? 600 : 400,
                              border: "none",
                              borderBottom:
                                active?.id === t.id
                                  ? "2px solid #1565C0"
                                  : "2px solid transparent",
                              background: "none",
                              color: active?.id === t.id ? "#1565C0" : "#666",
                              cursor: "pointer",
                            }}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    )}
                    <div
                      style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "24px 32px",
                      }}
                    >
                      {!showTabBar && (
                        <p
                          style={{
                            fontSize: "17px",
                            fontWeight: 500,
                            marginBottom: "20px",
                          }}
                        >
                          {localSchema?.label}
                        </p>
                      )}
                      {isEmpty && (
                        <p
                          style={{
                            fontSize: "13px",
                            color: "#aaa",
                            textAlign: "center",
                            padding: "40px",
                          }}
                        >
                          Aucun champ dans cette section.
                        </p>
                      )}
                      {active && active.fields.length > 0 && (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: "14px",
                            marginBottom: active.groups.length > 0 ? "18px" : 0,
                            maxWidth: showTabBar ? undefined : "480px",
                            margin: showTabBar ? undefined : "0 auto",
                          }}
                        >
                          {active.fields.map((f) => (
                            <div
                              key={f.id}
                              style={
                                f.type === "separator"
                                  ? { gridColumn: "1 / -1" }
                                  : undefined
                              }
                            >
                              {renderPreviewField(f)}
                            </div>
                          ))}
                        </div>
                      )}
                      {active?.groups.map(({ group, fields: groupFields }) => {
                        if (groupFields.length === 0) return null;
                        const isCollapsible =
                          group.variant === "accordion" ||
                          group.variant === "collapse";
                        const isOpen = previewOpenGroups[group.id] ?? true;
                        return (
                          <div
                            key={group.id}
                            style={{
                              border:
                                group.variant === "paper"
                                  ? "0.5px solid #e0e0e0"
                                  : "none",
                              borderRadius: "8px",
                              marginBottom: "14px",
                              overflow: "hidden",
                              background:
                                group.variant === "paper"
                                  ? "#fafafa"
                                  : "transparent",
                            }}
                          >
                            {group.label && (
                              <div
                                onClick={() =>
                                  isCollapsible &&
                                  setPreviewOpenGroups((p) => ({
                                    ...p,
                                    [group.id]: !isOpen,
                                  }))
                                }
                                style={{
                                  padding: "10px 14px",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: "#1565C0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  cursor: isCollapsible ? "pointer" : "default",
                                  borderBottom:
                                    isOpen && group.variant === "paper"
                                      ? "0.5px solid #e0e0e0"
                                      : "none",
                                }}
                              >
                                {group.label}
                                {isCollapsible && (
                                  <span>{isOpen ? "▾" : "▸"}</span>
                                )}
                              </div>
                            )}
                            {isOpen && (
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: `repeat(${group.columns}, 1fr)`,
                                  gap: "14px",
                                  padding: "14px",
                                }}
                              >
                                {groupFields.map((f) => (
                                  <div
                                    key={f.id}
                                    style={{
                                      gridColumn:
                                        f.type === "separator"
                                          ? "1 / -1"
                                          : `span ${Math.min(f.colSpan || 1, group.columns)}`,
                                    }}
                                  >
                                    {renderPreviewField(f)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (
            <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
              <aside
                style={{
                  width: "155px",
                  flexShrink: 0,
                  borderRight: "0.5px solid #eee",
                  padding: "10px 8px",
                  overflowY: "auto",
                }}
              >
                <p
                  style={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#666",
                    textTransform: "uppercase",
                    letterSpacing: ".05em",
                    margin: "0 0 8px",
                  }}
                >
                  Types de champs
                </p>
                {FIELD_TYPES_LOCAL.map(({ type, label }) => (
                  <div
                    key={type}
                    onClick={() => addField(type)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "7px",
                      padding: "6px 8px",
                      marginBottom: "3px",
                      border: "0.5px solid #eee",
                      borderRadius: "6px",
                      background: "#fff",
                      cursor: "pointer",
                      userSelect: "none",
                      fontSize: "12px",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#fff")
                    }
                  >
                    {label}
                  </div>
                ))}
              </aside>
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "10px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    marginBottom: "6px",
                    paddingBottom: "8px",
                    borderBottom: "0.5px solid #eee",
                  }}
                >
                  <input
                    value={localSchema?.label ?? ""}
                    placeholder="Nom du formulaire"
                    onChange={(e) =>
                      setLocalSchema((p) =>
                        p ? { ...p, label: e.target.value } : p,
                      )
                    }
                    style={{ flex: 1, ...inpSt, fontWeight: 500 }}
                  />
                </div>
                {(localSchema?.fields.length ?? 0) === 0 && (
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      color: "#aaa",
                      fontSize: "13px",
                      padding: "40px",
                    }}
                  >
                    <span style={{ fontSize: "36px" }}>↙</span>
                    Cliquez sur un type de champ dans la liste à gauche
                  </div>
                )}
                {localSchema?.fields.map((field, idx) => (
                  <div
                    key={field.id}
                    draggable
                    onDragStart={() => {
                      dragSrcIdx.current = idx;
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (
                        dragSrcIdx.current !== null &&
                        dragSrcIdx.current !== idx
                      )
                        moveField(dragSrcIdx.current, idx);
                      dragSrcIdx.current = null;
                    }}
                    onClick={() => setSelectedFieldId(field.id)}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      padding: "9px 10px",
                      border: `0.5px solid ${selectedFieldId === field.id ? "#1565C0" : "#eee"}`,
                      borderRadius: "7px",
                      background: "#fff",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <span
                      style={{
                        color: "#ccc",
                        cursor: "grab",
                        paddingTop: "2px",
                        fontSize: "16px",
                      }}
                    >
                      ⠿
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: 500,
                          marginBottom: "3px",
                          display: "flex",
                          gap: "4px",
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        {field.label}
                        {field.required && (
                          <span
                            style={{
                              fontSize: "10px",
                              background: "#E6F1FB",
                              color: "#185FA5",
                              border: "0.5px solid #B5D4F4",
                              borderRadius: "4px",
                              padding: "1px 5px",
                            }}
                          >
                            Requis
                          </span>
                        )}
                        {field.hidden && (
                          <span
                            style={{
                              fontSize: "10px",
                              background: "#f5f5f5",
                              color: "#999",
                              borderRadius: "4px",
                              padding: "1px 5px",
                            }}
                          >
                            Caché
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#999",
                          background: "#f5f5f5",
                          borderRadius: "4px",
                          padding: "2px 6px",
                          display: "inline-block",
                        }}
                      >
                        {
                          FIELD_TYPES_LOCAL.find((t) => t.type === field.type)
                            ?.label
                        }{" "}
                        ·{" "}
                        <code style={{ fontFamily: "monospace" }}>
                          {field.code}
                        </code>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "2px" }}>
                      <button
                        title="Dupliquer"
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateField(field.id);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "3px 5px",
                          borderRadius: "4px",
                          color: "#666",
                          fontSize: "13px",
                        }}
                      >
                        ⧉
                      </button>
                      <button
                        title="Supprimer"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeField(field.id);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "3px 5px",
                          borderRadius: "4px",
                          color: "#E24B4A",
                          fontSize: "13px",
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <aside
                style={{
                  width: "225px",
                  flexShrink: 0,
                  borderLeft: "0.5px solid #eee",
                  overflowY: "auto",
                  padding: "10px 12px",
                }}
              >
                {!selectedField ? (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#aaa",
                      textAlign: "center",
                      marginTop: "24px",
                    }}
                  >
                    Sélectionnez un champ pour éditer ses propriétés
                  </p>
                ) : (
                  <FieldPropsLocal
                    field={selectedField}
                    allFields={localSchema?.fields ?? []}
                    layout={localSchema?.layout}
                    onChange={(patch) => updateField(selectedField.id, patch)}
                  />
                )}
              </aside>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const FieldPropsLocal: React.FC<{
  field: FormField;
  allFields: FormField[];
  layout?: { sections: LayoutSection[] };
  onChange: (patch: Partial<FormField>) => void;
}> = ({ field, onChange, allFields, layout }) => {
  const hasOptions = ["select", "multiselect", "radio"].includes(field.type);
  const isSep = field.type === "separator";
  const others = allFields.filter(
    (f) => f.id !== field.id && f.type !== "separator",
  );

  const inpSt: React.CSSProperties = {
    width: "100%",
    fontSize: "13px",
    padding: "5px 8px",
    border: "0.5px solid #ccc",
    borderRadius: "6px",
    background: "#f5f5f5",
    color: "#222",
    boxSizing: "border-box",
  };
  const secLbl: React.CSSProperties = {
    fontSize: "11px",
    fontWeight: 500,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: ".05em",
    margin: 0,
  };

  const addOption = () => {
    const opts = [...(field.options ?? [])];
    opts.push({
      label: "Option " + (opts.length + 1),
      value: "option_" + (opts.length + 1),
    });
    onChange({ options: opts });
  };
  const updOpt = (i: number, patch: Partial<SelectOption>) => {
    const opts = [...(field.options ?? [])];
    opts[i] = { ...opts[i], ...patch };
    onChange({ options: opts });
  };
  const delOpt = (i: number) => {
    const opts = [...(field.options ?? [])];
    opts.splice(i, 1);
    onChange({ options: opts });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
      <p style={secLbl}>Propriétés</p>
      {!isSep && (
        <>
          <div>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Label
            </label>
            <input
              style={inpSt}
              value={field.label}
              onChange={(e) => onChange({ label: e.target.value })}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Clé (code)
            </label>
            <input
              style={{ ...inpSt, fontFamily: "monospace", fontSize: "12px" }}
              value={field.code}
              onChange={(e) => onChange({ code: slugify(e.target.value) })}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Type
            </label>
            <select
              style={inpSt}
              value={field.type}
              onChange={(e) => onChange({ type: e.target.value as FieldType })}
            >
              {FIELD_TYPES_LOCAL.filter((t) => t.type !== "separator").map(
                (t) => (
                  <option key={t.type} value={t.type}>
                    {t.label}
                  </option>
                ),
              )}
            </select>
          </div>
          <div>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Placeholder
            </label>
            <input
              style={inpSt}
              value={field.placeholder ?? ""}
              onChange={(e) => onChange({ placeholder: e.target.value })}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Valeur défaut
            </label>
            <input
              style={inpSt}
              value={field.defaultValue ?? ""}
              onChange={(e) => onChange({ defaultValue: e.target.value })}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Texte d'aide
            </label>
            <input
              style={inpSt}
              value={field.helpText ?? ""}
              onChange={(e) => onChange({ helpText: e.target.value })}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {[
              { label: "Requis", key: "required" },
              { label: "Lecture seule", key: "readOnly" },
              { label: "Caché", key: "hidden" },
            ].map(({ label, key }) => (
              <label
                key={key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!(field as any)[key]}
                  onChange={(e) => onChange({ [key]: e.target.checked } as any)}
                  style={{ accentColor: "#185FA5" }}
                />
                {label}
              </label>
            ))}
          </div>
        </>
      )}
      {field.type === "apiAutocomplete" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
          <p style={secLbl}>Configuration API</p>

          <div>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Méthode
            </label>
            <select
              style={inpSt}
              value={field.apiMethod || "GET"}
              onChange={(e) =>
                onChange({ apiMethod: e.target.value as "GET" | "POST" })
              }
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
            </select>
          </div>

          <div>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "3px",
              }}
            >
              URL de l'API
            </label>
            <input
              style={{ ...inpSt, fontFamily: "monospace", fontSize: "12px" }}
              value={field.apiUrl || ""}
              placeholder="https://.../api/recherche?q={{codeClient}}"
              onChange={(e) => onChange({ apiUrl: e.target.value })}
            />
          </div>

          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "5px",
              }}
            >
              <label style={{ fontSize: "11px", color: "#666" }}>
                Paramètres (query)
              </label>
              <button
                onClick={() =>
                  onChange({
                    apiParams: [
                      ...(field.apiParams || []),
                      { key: "", value: "" },
                    ],
                  })
                }
                style={{
                  fontSize: "11px",
                  padding: "2px 7px",
                  border: "0.5px solid #ccc",
                  borderRadius: "6px",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                + Ajouter
              </button>
            </div>
            {(field.apiParams || []).map((p, i) => (
              <div
                key={i}
                style={{ display: "flex", gap: "4px", marginBottom: "4px" }}
              >
                <input
                  style={{
                    ...inpSt,
                    flex: 1,
                    fontSize: "12px",
                    fontFamily: "monospace",
                  }}
                  value={p.key}
                  placeholder="clé"
                  onChange={(e) => {
                    const next = [...(field.apiParams || [])];
                    next[i] = { ...next[i], key: e.target.value };
                    onChange({ apiParams: next });
                  }}
                />
                <input
                  style={{
                    ...inpSt,
                    flex: 1,
                    fontSize: "12px",
                    fontFamily: "monospace",
                  }}
                  value={p.value}
                  placeholder="valeur ou {{champ}}"
                  onChange={(e) => {
                    const next = [...(field.apiParams || [])];
                    next[i] = { ...next[i], value: e.target.value };
                    onChange({ apiParams: next });
                  }}
                />
                <button
                  onClick={() => {
                    const next = [...(field.apiParams || [])];
                    next.splice(i, 1);
                    onChange({ apiParams: next });
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#E24B4A",
                    fontSize: "13px",
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <div>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Chemin des résultats dans la réponse
            </label>
            <input
              style={{ ...inpSt, fontFamily: "monospace", fontSize: "12px" }}
              value={field.itemsPath || ""}
              placeholder="{{data.body}}"
              onChange={(e) => onChange({ itemsPath: e.target.value })}
            />
          </div>

          <div>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Modèle du libellé affiché
            </label>
            <input
              style={{ ...inpSt, fontFamily: "monospace", fontSize: "12px" }}
              value={field.labelTemplate || ""}
              placeholder={`{{${field.code}}} - {{nom}} {{prenom}}`}
              onChange={(e) => onChange({ labelTemplate: e.target.value })}
            />
          </div>

          <div>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Modèle de la valeur stockée
            </label>
            <input
              style={{ ...inpSt, fontFamily: "monospace", fontSize: "12px" }}
              value={field.valueTemplate || ""}
              placeholder={`{{${field.code}}}`}
              onChange={(e) => onChange({ valueTemplate: e.target.value })}
            />
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={!!field.freeSolo}
              onChange={(e) => onChange({ freeSolo: e.target.checked })}
              style={{ accentColor: "#185FA5" }}
            />
            Saisie libre autorisée (freeSolo)
          </label>

          <div>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "5px",
              }}
            >
              Champs à auto-remplir après sélection
            </label>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                maxHeight: "160px",
                overflowY: "auto",
              }}
            >
              {allFields
                .filter((f) => f.id !== field.id)
                .map((f) => {
                  const checked = (field.linkedFieldCodes || []).includes(
                    f.code,
                  );
                  return (
                    <label
                      key={f.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "12px",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const current = field.linkedFieldCodes || [];
                          const next = e.target.checked
                            ? [...current, f.code]
                            : current.filter((c) => c !== f.code);
                          onChange({ linkedFieldCodes: next });
                        }}
                        style={{ accentColor: "#185FA5" }}
                      />
                      <code style={{ fontFamily: "monospace" }}>{f.code}</code>{" "}
                      — {f.label}
                    </label>
                  );
                })}
              {allFields.length <= 1 && (
                <p
                  style={{
                    fontSize: "11px",
                    color: "#aaa",
                    fontStyle: "italic",
                  }}
                >
                  Ajoutez d'autres champs au formulaire pour pouvoir les lier
                  ici.
                </p>
              )}
            </div>
            <p style={{ fontSize: "10px", color: "#999", marginTop: "4px" }}>
              Ces champs seront remplis automatiquement à partir des propriétés
              de même nom dans l'item sélectionné (renvoyé par l'API).
            </p>
          </div>
        </div>
      )}
      {layout && layout.sections.length > 0 && !isSep && (
        <div>
          <p style={{ ...secLbl, marginTop: "4px", marginBottom: "5px" }}>
            Emplacement
          </p>
          <div style={{ marginBottom: "6px" }}>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Onglet
            </label>
            <select
              style={inpSt}
              value={field.sectionId || ""}
              onChange={(e) =>
                onChange({
                  sectionId: e.target.value || undefined,
                  groupId: undefined,
                })
              }
            >
              <option value="">— Général (par défaut) —</option>
              {layout.sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          {field.sectionId && (
            <div style={{ marginBottom: "6px" }}>
              <label
                style={{
                  fontSize: "11px",
                  color: "#666",
                  display: "block",
                  marginBottom: "3px",
                }}
              >
                Groupe
              </label>
              <select
                style={inpSt}
                value={field.groupId || ""}
                onChange={(e) =>
                  onChange({ groupId: e.target.value || undefined })
                }
              >
                <option value="">— aucun —</option>
                {(
                  layout.sections.find((s) => s.id === field.sectionId)
                    ?.groups || []
                ).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {field.groupId && (
            <div>
              <label
                style={{
                  fontSize: "11px",
                  color: "#666",
                  display: "block",
                  marginBottom: "3px",
                }}
              >
                Largeur (colonnes occupées)
              </label>
              <input
                type="number"
                min={1}
                max={4}
                style={inpSt}
                value={field.colSpan || 1}
                onChange={(e) => onChange({ colSpan: Number(e.target.value) })}
              />
            </div>
          )}
        </div>
      )}
      {hasOptions && (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "5px",
            }}
          >
            <p style={secLbl}>Options</p>
            <button
              onClick={addOption}
              style={{
                fontSize: "11px",
                padding: "2px 7px",
                border: "0.5px solid #ccc",
                borderRadius: "6px",
                background: "#fff",
                color: "#222",
                cursor: "pointer",
              }}
            >
              + Ajouter
            </button>
          </div>
          {(field.options ?? []).map((opt, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: "4px",
                marginBottom: "4px",
                alignItems: "center",
              }}
            >
              <input
                style={{ ...inpSt, flex: 1, fontSize: "12px" }}
                value={opt.label}
                placeholder="Label"
                onChange={(e) =>
                  updOpt(i, {
                    label: e.target.value,
                    value: slugify(e.target.value) || opt.value,
                  })
                }
              />
              <button
                onClick={() => delOpt(i)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "3px 5px",
                  borderRadius: "4px",
                  color: "#E24B4A",
                  fontSize: "13px",
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
      {!isSep && others.length > 0 && (
        <div>
          <p style={{ ...secLbl, marginTop: "4px", marginBottom: "5px" }}>
            Affichage conditionnel
          </p>
          <div style={{ marginBottom: "6px" }}>
            <label
              style={{
                fontSize: "11px",
                color: "#666",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Visible si champ
            </label>
            <select
              style={inpSt}
              value={field.conditionField ?? ""}
              onChange={(e) =>
                onChange({ conditionField: e.target.value || undefined })
              }
            >
              <option value="">— aucun —</option>
              {others.map((f) => (
                <option key={f.id} value={f.code}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          {field.conditionField && (
            <div>
              <label
                style={{
                  fontSize: "11px",
                  color: "#666",
                  display: "block",
                  marginBottom: "3px",
                }}
              >
                Vaut
              </label>
              <input
                style={inpSt}
                value={field.conditionValue ?? ""}
                placeholder="valeur"
                onChange={(e) => onChange({ conditionValue: e.target.value })}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AttachJsonPage: React.FC = () => {
  const modelerRef = useRef<BpmnModeler | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [jsonData, setJsonData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [aiPrompt, setAiPrompt] = useState<string>("");
  const [isAiLoading, setIsAiLoading] = useState<boolean>(false);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true);

  const [canEditSensitiveNodes, setCanEditSensitiveNodes] =
    useState<boolean>(false);
  const canEditRef = useRef<boolean>(false);

  const [formsState, setFormsState] = useState<
    Record<string, DynamicFormSchemaLocal>
  >({});
  const [formsIsOpen, setFormsIsOpen] = useState<boolean>(false);
  const [formsEditingId, setFormsEditingId] = useState<string | null>(null);
  const formsRef = useRef<Record<string, DynamicFormSchemaLocal>>({});
  const isApplyingFromBuilderRef = useRef<boolean>(false);
  const aiPromptRef = useRef<string>("");

  const persistForms = (next: Record<string, DynamicFormSchemaLocal>) => {
    formsRef.current = next;
    setFormsState(next);
    try {
      const m = modelerRef.current as any;
      if (m) {
        const canvas = m.get("canvas");
        const modeling = m.get("modeling");
        const elementRegistry = m.get("elementRegistry") as any;
        const formsForBpmn: Record<string, any> = {};
        Object.entries(next).forEach(([id, form]) => {
          const f = form as any;
          if (f.fields && (!f.schema || !f.uischema)) {
            f.schema = fieldsToJsonSchema(f.fields);
            f.uischema = fieldsToUiSchema(f.fields, f.label, f.layout);
          }
          formsForBpmn[id] = {
            id: f.id,
            code: f.code || f.id,
            label: f.label,
            schema: f.schema,
            uischema: f.uischema,
            scripts: f.scripts || [],
            transformers: f.transformers || [],
            updatedAt: f.updatedAt,
            ...f,
          };
        });
        const formsJson = JSON.stringify(formsForBpmn);

        isApplyingFromBuilderRef.current = true;
        const root = canvas.getRootElement();
        modeling.updateProperties(root, { forms: formsJson });
        const allEls = elementRegistry.getAll();
        const processShape = allEls.find(
          (el: any) => el.businessObject?.$type === "bpmn:Process",
        );
        if (processShape) {
          modeling.updateProperties(processShape, { forms: formsJson });
        }
        isApplyingFromBuilderRef.current = false;
      }
    } catch (err) {
      console.error("Erreur persistForms:", err);
    }
  };

  const initFormsFromProcess = (processData: any) => {
    try {
      const raw = safeParseFormsField(processData?.forms);
      if (!raw || Object.keys(raw).length === 0) return;
      const normalized: Record<string, DynamicFormSchemaLocal> = {};
      for (const [id, val] of Object.entries(raw as Record<string, any>)) {
        if (!val || typeof val !== "object") continue;
        let fields: FormField[] = [];
        if (Array.isArray(val.fields) && val.fields.length > 0) {
          fields = val.fields;
        } else if (
          val.schema?.properties &&
          Object.keys(val.schema.properties).length > 0
        ) {
          fields = schemaToFields(val.schema, val.uischema);
        }
        normalized[id] = {
          ...val,
          id: val.id || id,
          label: val.label || id,
          fields,
          updatedAt: val.updatedAt || new Date().toISOString(),
        };
      }
      formsRef.current = normalized;
      setFormsState(normalized);
    } catch (e) {
      console.error("Erreur initFormsFromProcess:", e);
    }
  };

  const formManager: FormManagerApi = {
    forms: formsState,
    isOpen: formsIsOpen,
    editingFormId: formsEditingId,
    formList: Object.values(formsState).sort((a, b) =>
      a.label.localeCompare(b.label),
    ),
    openManager: (editId?: string) => {
      setFormsEditingId(editId ?? null);
      setFormsIsOpen(true);
    },
    closeManager: () => {
      setFormsIsOpen(false);
      setFormsEditingId(null);
    },
    createForm: () => {
      const id = uid();
      const schema: DynamicFormSchemaLocal = {
        id,
        label: "Nouveau formulaire",
        fields: [],
        updatedAt: new Date().toISOString(),
      };
      persistForms({ ...formsRef.current, [id]: schema });
      setFormsEditingId(id);
    },
    deleteForm: (id: string) => {
      const next = { ...formsRef.current };
      delete next[id];
      persistForms(next);
      if (formsEditingId === id) setFormsEditingId(null);
    },
    saveForm: (schema: DynamicFormSchemaLocal) => {
      const updated = { ...schema, updatedAt: new Date().toISOString() };
      persistForms({ ...formsRef.current, [schema.id]: updated });
    },
    setEditingId: (id: string | null) => setFormsEditingId(id),
    getFormById: (id: string) => formsRef.current[id] ?? null,
    getFormsAsJson: () => JSON.stringify(formsRef.current, null, 2),
  };

  useEffect(() => {
    canEditRef.current = canEditSensitiveNodes;
  }, [canEditSensitiveNodes]);

  const [isCreateMode] = useState<boolean>(() => {
    return new URLSearchParams(window.location.search).get("mode") === "create";
  });
  const [pendingProcessData, setPendingProcessData] = useState<any>(null);
  const [isFromExternalProcess, setIsFromExternalProcess] = useState<boolean>(
    () => {
      const params = new URLSearchParams(window.location.search);
      return (
        params.get("mode") === "create" || params.get("loading") === "true"
      );
    },
  );
  const [saveNotification, setSaveNotification] = useState<{
    open: boolean;
    message: string;
    type: "success" | "error";
  }>({ open: false, message: "", type: "success" });
  const [showFilterWarning, setShowFilterWarning] = useState<boolean>(false);
  const [isLoadingProcess, setIsLoadingProcess] = useState<boolean>(() => {
    return (
      new URLSearchParams(window.location.search).get("loading") === "true"
    );
  });

  useEffect(() => {
    return () => {
      if (modelerRef.current) {
        modelerRef.current.destroy();
        modelerRef.current = null;
      }
      removeReadonlyFromPanel();
    };
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      console.error(
        "Timeout — pas de token reçu depuis webADVANCED_PROCESS_MANAGEMENT",
      );
      setIsAuthorized(false);
      setIsCheckingAuth(false);
    }, 15000);

    const handleAuth = async (event: MessageEvent) => {
      if (event.data?.type === "AUTH_TOKEN") {
        clearTimeout(timeout);
        const token = event.data.token;
        if (!token || !isTokenValid(token)) {
          setIsAuthorized(false);
        } else {
          sessionStorage.setItem("WFE_AUTH_TOKEN", token);
          setIsAuthorized(true);
          try {
            const payload = JSON.parse(atob(token.split(".")[1]));
            const roles: string[] = Array.isArray(payload.roles)
              ? payload.roles
              : payload.role
                ? [payload.role]
                : [];
            const canEdit = roles.includes("ADVANCED_PROCESS_MANAGEMENT2");
            setCanEditSensitiveNodes(canEdit);
            canEditRef.current = canEdit;
          } catch {}
        }
        setIsCheckingAuth(false);
      }
    };

    window.addEventListener("message", handleAuth);
    const parent = window.parent !== window ? window.parent : window.opener;
    if (parent) parent.postMessage({ type: "BPMN_READY" }, "*");
    return () => {
      window.removeEventListener("message", handleAuth);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type !== "LOAD_PROCESS") return;
      try {
        const payload = event.data.payload;
        if (!payload) return;

        const processData = payload.json ?? payload; // fallback si jamais un ancien format arrive encore
        const xml = payload.xml;

        if (!processData) return;

        setIsLoadingProcess(true);
        setIsFromExternalProcess(true);
        setJsonData(processData);
        setError(null);
        initFormsFromProcess(processData);

        const hasStoredXml = typeof xml === "string" && xml.trim().length > 0;

        if (hasStoredXml) {
          await loadModeling(xml);
        } else {
          await prepareModeling(processData);
        }
      } catch (err) {
        setError("Erreur lors du chargement du processus reçu.");
      } finally {
        setTimeout(() => setIsLoadingProcess(false), 1000);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  var jumpx = 1;
  var topLevel = 100;
  var registries = new Array<RegsitryLink>();
  const topLevel1 = 100;
  const topLevel2 = 200;
  const lefLevel = 180;
  var dir = 1;
  var jump: boolean = true;
  function attachFormsSync(instance: BpmnModeler) {
    try {
      const eventBus = instance.get("eventBus") as any;
      const elementRegistry = instance.get("elementRegistry") as any;

      const syncFromBpmn = () => {
        try {
          if (isApplyingFromBuilderRef.current) return;

          const allEls = elementRegistry.getAll();
          const processShape = allEls.find(
            (el: any) => el.businessObject?.$type === "bpmn:Process",
          );
          const rootBo = processShape?.businessObject as any;

          const raw = rootBo?.forms;

          if (raw === undefined || raw === null) return;

          const parsed = safeParseFormsField(raw);
          if (!parsed || Object.keys(parsed).length === 0) return;

          const normalized: Record<string, DynamicFormSchemaLocal> = {};
          for (const [id, val] of Object.entries(
            parsed as Record<string, any>,
          )) {
            if (!val || typeof val !== "object") continue;
            let fields: FormField[] = [];
            if (
              Array.isArray((val as any).fields) &&
              (val as any).fields.length > 0
            ) {
              fields = (val as any).fields;
            } else if (
              (val as any).schema?.properties &&
              Object.keys((val as any).schema.properties).length > 0
            ) {
              fields = schemaToFields(
                (val as any).schema,
                (val as any).uischema,
              );
            }
            normalized[id] = {
              ...(val as any),
              id: (val as any).id || id,
              label: (val as any).label || id,
              fields,
              updatedAt: (val as any).updatedAt || new Date().toISOString(),
            };
          }

          formsRef.current = normalized;
          setFormsState(normalized);
        } catch (e) {
          console.error("Erreur syncFromBpmn:", e);
        }
      };

      eventBus.on("commandStack.changed", syncFromBpmn);
    } catch {}
  }
  function createModeler(): BpmnModeler {
    if (modelerRef.current) {
      modelerRef.current.destroy();
      modelerRef.current = null;
    }
    const canvasEl = document.getElementById("js-canvas");
    if (!canvasEl)
      throw new Error("Canvas container #js-canvas introuvable dans le DOM");

    const formPaletteModule = createFormPaletteModule(
      () => formManager.openManager(),
      () => formManager.formList.length,
    );
    const hideScriptIconModule = {
      __init__: ["hideScriptIcon"],
      hideScriptIcon: [
        "type",
        function HideScriptIcon(this: any, eventBus: any) {
          const R = 10,
            FILL = "#ffffff",
            STROKE = "#000000",
            SW = 2;
          const FONT_SIZE = 12,
            FONT = "Arial,sans-serif",
            LH = 1.3,
            PAD = 8;
          function mkEl(tag: string, attrs: Record<string, string | number>) {
            const n = document.createElementNS(
              "http://www.w3.org/2000/svg",
              tag,
            );
            Object.entries(attrs).forEach(([k, v]) =>
              n.setAttribute(k, String(v)),
            );
            return n;
          }
          function wrap(text: string, maxW: number): string[] {
            const chars = Math.max(1, Math.floor(maxW / (FONT_SIZE * 0.55)));
            const lines: string[] = [];
            let cur = "";
            (text || "").split(" ").forEach((w) => {
              const c = cur ? `${cur} ${w}` : w;
              if (c.length > chars && cur) {
                lines.push(cur);
                cur = w;
              } else cur = c;
            });
            if (cur) lines.push(cur);
            return lines;
          }
        },
      ],
    };
    (hideScriptIconModule.hideScriptIcon[1] as any).$inject = ["eventBus"];
    const frenchTranslateModule = {
      translate: [
        "value",
        (template: string, replacements?: Record<string, string>): string => {
          const t: Record<string, string> = {
            "Activate hand tool": "Outil main",
            "Activate lasso tool": "Outil lasso",
            "Activate create/remove space tool": "Outil espace",
            "Activate global connect tool": "Outil de connexion globale",
            "Create start event": "Créer un événement de début",
            "Create intermediate/boundary event":
              "Créer un événement intermédiaire",
            "Create end event": "Créer un événement de fin",
            "Create gateway": "Créer une passerelle",
            "Create task": "Créer une tâche",
            "Create data object reference": "Créer un objet de données",
            "Create data store reference": "Créer un stockage de données",
            "Create expanded sub-process": "Créer un sous-processus développé",
            "Create pool/participant": "Créer un pool/participant",
            "Create group": "Créer un groupe",
            "Append {type}": "Ajouter {type}",
            "Append EndEvent": "Ajouter un événement de fin",
            "Append Task": "Ajouter une tâche",
            "Append Gateway": "Ajouter une passerelle",
            "Append IntermediateThrowEvent":
              "Ajouter un événement intermédiaire",
            "Change type": "Changer le type",
            "Connect using Association": "Connecter (association)",
            "Connect using Sequence/MessageFlow or Association":
              "Connecter (flux ou association)",
            "Connect using DataInputAssociation":
              "Connecter (association de données)",
            Delete: "Supprimer",
            Edit: "Modifier",
            "Edit type": "Modifier le type",
            Remove: "Supprimer",
            "Reconnect start": "Reconnecter le départ",
            "Reconnect end": "Reconnecter la fin",
            "Set color": "Définir la couleur",
            "Toggle collapse": "Réduire/développer",
            "Divide into two Lanes": "Diviser en deux couloirs",
            "Divide into three Lanes": "Diviser en trois couloirs",
            "Add Lane above": "Ajouter un couloir au-dessus",
            "Add Lane below": "Ajouter un couloir en-dessous",
            "Expand SubProcess": "Développer le sous-processus",
            "Collapse SubProcess": "Réduire le sous-processus",
            Task: "Tâche",
            UserTask: "Tâche utilisateur",
            ServiceTask: "Tâche de service",
            ScriptTask: "Tâche script",
            BusinessRuleTask: "Tâche règle métier",
            ManualTask: "Tâche manuelle",
            SendTask: "Tâche d'envoi",
            ReceiveTask: "Tâche de réception",
            CallActivity: "Activité d'appel",
            SubProcess: "Sous-processus",
            ExclusiveGateway: "Passerelle exclusive",
            ParallelGateway: "Passerelle parallèle",
            InclusiveGateway: "Passerelle inclusive",
            EventBasedGateway: "Passerelle événementielle",
            ComplexGateway: "Passerelle complexe",
            StartEvent: "Événement de début",
            EndEvent: "Événement de fin",
            IntermediateCatchEvent: "Événement intermédiaire (capture)",
            IntermediateThrowEvent: "Événement intermédiaire (lancement)",
            BoundaryEvent: "Événement frontière",
            Lane: "Couloir",
            DataObjectReference: "Objet de données",
            DataStoreReference: "Stockage de données",
            TextAnnotation: "Annotation",
            Message: "Message",
            Signal: "Signal",
            Error: "Erreur",
            Escalation: "Escalade",
            Timer: "Minuterie",
            Conditional: "Conditionnel",
            Link: "Lien",
            Compensation: "Compensation",
            Cancel: "Annulation",
            Terminate: "Terminaison",
            None: "Aucun",
          };
          const translated = t[template] ?? template;
          if (!replacements) return translated;
          return translated.replace(/\{(\w+)\}/g, (_: string, key: string) => {
            const val = replacements[key];
            return val !== undefined ? (t[val] ?? val) : `{${key}}`;
          });
        },
      ],
    };
    const instance = new BpmnModeler({
      container: "#js-canvas",
      propertiesPanel: { parent: "#js-properties-panel" },
      additionalModules: [
        BpmnPropertiesPanelModule,
        BpmnPropertiesProviderModule,
        CaseModule,
        frenchTranslateModule,
        formPaletteModule,
      ],
      moddleExtensions: { caseModdleExtension },
    }) as BpmnModeler;
    modelerRef.current = instance;
    return instance;
  }

  function patchScrollAfterImport(instance: BpmnModeler): void {
    try {
      const canvas = instance.get("canvas") as any;
      const _scroll = canvas.scroll.bind(canvas);
      canvas.scroll = (delta: any) => {
        try {
          _scroll(delta);
        } catch {}
      };
    } catch {}
  }

  const loadOldJson = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const result: ProcessDefinition = JSON.parse(
            e.target?.result as string,
          );
          setJsonData(result);
          initFormsFromProcess(result);
          prepareModeling(result);
          setError(null);
        } catch (err) {
          setError("Invalid JSON file");
          setJsonData(null);
        }
      };
      reader.readAsText(file);
      event.target.value = "";
    }
  };

  const loadMakinaXml = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const result = e.target?.result as string;
          setJsonData(result);
          loadModeling(result);
          setError(null);
        } catch (err) {
          setError("Invalid XML file");
          setJsonData(null);
        }
      };
      reader.readAsText(file);
      event.target.value = "";
    }
  };

  async function prepareModeling(data: ProcessDefinition): Promise<void> {
    const instance = createModeler();
    attachFormsSync(instance);
    const container = $("#js-drop-zone");
    const hasNodes = data.nodes && Object.keys(data.nodes).length > 0;
    const hasLinks = data.links && data.links.length > 0;
    if (!hasNodes && !hasLinks && (data as any).definitions) {
      const defRootElements = (data as any).definitions?.rootElements || [];
      const processEl = findProcessElement(defRootElements);
      if (processEl?.flowElements?.length) {
        rebuildFromDefinitions(
          data,
          processEl.flowElements,
          (data as any).definitions,
        );
      }
    }
    const firstLink = (data.links || [])[0];
    const startCode = firstLink?.source?.node || "";
    jumpx = 1;
    topLevel = 100;
    registries = [];
    dir = 1;

    try {
      await instance.importXML(diagramXML);
      patchScrollAfterImport(instance);
      container.removeClass("with-error").addClass("with-diagram");

      const overlays = instance.get("overlays") as any;
      const canvas = instance.get("canvas") as any;
      const elementRegistry = instance.get("elementRegistry") as any;
      const modeling = instance.get("modeling") as any;
      const rootElement = canvas.getRootElement();
      const elementFactory = instance.get("elementFactory") as any;
      const process = elementRegistry.get("Process_1") as any;

      modeling.updateProperties(process, { id: data.code });
      addProcessExtentionProperties(process, data);

      const flowElementsIndex: Record<string, string> = {};
      const defRootElements = (data as any).definitions?.rootElements || [];
      const processEl = findProcessElement(defRootElements);
      const flowElements = processEl?.flowElements || [];
      flowElements.forEach((f: any) => {
        if (f.id && f.name) flowElementsIndex[f.id] = f.name;
      });

      const startEvent = modeling.createShape(
        { type: "bpmn:StartEvent" },
        { x: 300, y: 80 },
        rootElement,
      );
      const startEl = flowElements.find(
        (f: any) => f.$type === "bpmn:StartEvent",
      );
      startEvent.businessObject.name = startEl?.name || "Start";

      const specialRegistry: Record<string, string> = {};
      if (startEl?.id) specialRegistry[startEl.id] = startEvent.id;

      const endEventIds = new Set<string>();
      (data.links || []).forEach((link: any) => {
        const targetCode = link.target?.node || "";
        const flowEl = flowElements.find((f: any) => f.id === targetCode);
        if (flowEl?.$type === "bpmn:EndEvent") endEventIds.add(targetCode);
        const nodeData = (data.nodes || {})[targetCode];
        if ((nodeData as any)?.type === "endEvent") endEventIds.add(targetCode);
        const wfeMapping = resolveNodeBpmnMapping(
          (nodeData as any)?.type,
          (nodeData as any)?.service,
        );
        if (wfeMapping?.isEndEvent) endEventIds.add(targetCode);
      });

      endEventIds.forEach((endId) => {
        const flowEl = flowElements.find((f: any) => f.id === endId);
        const endLabel = flowEl?.name || flowElementsIndex[endId] || "End";
        const endNode = elementFactory.createShape({ type: "bpmn:EndEvent" });
        endNode.businessObject.name = endLabel;
        const nodeData = (data.nodes || {})[endId] as any;
        modeling.createShape(endNode, { x: 300, y: 400 }, process);
        addEndEventExtensionProperties(endNode, nodeData);
        specialRegistry[endId] = endNode.id;
      });

      modeling.updateProperties(process, { name: data.label });

      data?.links?.forEach((link: any, index: number) => {
        jump = true;
        let source = registries.find(
          (element) => element.node.code === link.source?.node,
        );
        let target = registries.find(
          (element) => element.node.code === link.target?.node,
        );
        const sourceSpecialId = specialRegistry[link.source?.node || ""];
        const targetSpecialId = specialRegistry[link.target?.node || ""];

        target?.isIfNode ? (jump = false) : (jump = true);
        if (registries.length % 15 === 0 && index > 0) {
          dir = -dir;
          topLevel = topLevel + 400;
        }

        if (source?.id) {
          source.output = link.source?.output || "output";
          if (source.node.type === NodeType.simple) {
            const mapping = resolveNodeBpmnMapping(
              source.node.type,
              source.node.service,
            );
            if (mapping?.isIfNode) source.isIfNode = true;
          }
        }

        if (!source?.id && !sourceSpecialId) {
          const nodeData = (data.nodes || {})[link.source?.node || ""];
          if (
            nodeData &&
            (nodeData as any).type !== "endEvent" &&
            (nodeData as any).type !== "startEvent"
          ) {
            const label =
              flowElementsIndex[link.source?.node || ""] ||
              (nodeData as any).label ||
              nodeData.code ||
              link.source?.node;
            const nodeElement = addNode(
              data,
              link.source?.node || "",
              link.source?.output || "output",
            );
            if (nodeElement.type) {
              const newTask = elementFactory.createShape({
                type: nodeElement.type,
              });
              newTask.businessObject.name = label || nodeElement.label;
              addActivityExtentionProperties(newTask, nodeElement.node);
              source = {
                id: newTask.id,
                isIfNode: nodeElement.isIfNode,
                node: nodeElement.node,
                output: nodeElement.output,
              } as RegsitryLink;
              const provisoryX = lefLevel * jumpx + 200;
              const provisoryY =
                registries.length % 2 === 0
                  ? topLevel + topLevel1
                  : topLevel + topLevel2;
              modeling.createShape(
                newTask,
                { x: provisoryX, y: provisoryY },
                process,
              );
              jumpx += dir;
              if (index === 0)
                modeling.connect(startEvent, newTask, {
                  type: "bpmn:SequenceFlow",
                });
              registries.push(source);
            }
          }
        }

        if (!target?.id && !targetSpecialId) {
          const nodeData = (data.nodes || {})[link.target?.node || ""];
          if (
            nodeData &&
            (nodeData as any).type !== "endEvent" &&
            (nodeData as any).type !== "startEvent"
          ) {
            const label =
              flowElementsIndex[link.target?.node || ""] ||
              (nodeData as any).label ||
              nodeData.code ||
              link.target?.node;
            const nodeElement = addNode(
              data,
              link.target?.node || "",
              link.target?.input || "input",
            );
            if (nodeElement.type) {
              const newTask = elementFactory.createShape({
                type: nodeElement.type,
              });
              newTask.businessObject.name = label || nodeElement.label;
              addActivityExtentionProperties(newTask, nodeElement.node);
              target = {
                id: newTask.id,
                isIfNode: nodeElement.isIfNode,
                node: nodeElement.node,
              } as RegsitryLink;
              const provisoryX = lefLevel * jumpx + 200;
              let provisoryY =
                registries.length % 2 === 0
                  ? topLevel + topLevel1
                  : topLevel + topLevel2;
              if (
                target.isIfNode &&
                index > 2 &&
                !registries[index - 3]?.isIfNode
              ) {
                provisoryY =
                  registries.length % 2 === 1
                    ? topLevel + topLevel1
                    : topLevel + topLevel2;
              }
              modeling.createShape(
                newTask,
                { x: provisoryX, y: provisoryY },
                process,
              );
              jumpx += dir;
              registries.push(target);
            }
          }
        }

        const sourceShapeId = source?.id || sourceSpecialId;
        const targetShapeId = target?.id || targetSpecialId;
        if (sourceShapeId && targetShapeId) {
          const sourceNode = elementRegistry.get(sourceShapeId);
          const targetNode = elementRegistry.get(targetShapeId);
          if (sourceNode && targetNode) {
            if (source?.isIfNode) {
              const createdFlow = modeling.connect(sourceNode, targetNode, {
                type: "bpmn:SequenceFlow",
              });
              modeling.updateProperties(createdFlow, {
                name: source.output,
                businessObject: {
                  conditionExpression: {
                    body: "${decision === ${source.output}}",
                    $type: "bpmn:FormalExpression",
                  },
                },
              });
            } else if (
              !link.source?.output ||
              link.source?.output === "output"
            ) {
              modeling.connect(sourceNode, targetNode, {
                type: "bpmn:SequenceFlow",
              });
            } else {
              const createdFlow = modeling.connect(sourceNode, targetNode, {
                type: "bpmn:SequenceFlow",
              });
              modeling.updateProperties(createdFlow, {
                name: link.source?.output,
              });
            }
          }
        }
      });

      try {
        const layouter = instance.get("layouter") as any;
        const success = await applyFullLayout(
          elementRegistry,
          modeling,
          canvas,
          layouter,
          data,
          startCode,
          instance,
        );
        if (!success) {
          console.warn(
            "[prepareModeling] applyFullLayout a échoué — zoom auto uniquement",
          );
          setTimeout(() => {
            try {
              fitDiagramToViewport(canvas, elementRegistry);
            } catch {}
          }, 100);
        }
      } catch (layoutErr) {
        console.error("[prepareModeling] Erreur layout :", layoutErr);
        setTimeout(() => {
          try {
            const canvas2 = instance.get("canvas") as any;
            const er2 = instance.get("elementRegistry") as any;
            fitDiagramToViewport(canvas2, er2);
          } catch {}
        }, 100);
      }

      const eventBus = instance.get("eventBus") as any;
      eventBus.on("selection.changed", ({ newSelection }: any) => {
        const banner = document.getElementById(
          "js-properties-panel-readonly-banner",
        );
        if (!canEditRef.current && newSelection?.length === 1) {
          const service = newSelection[0]?.businessObject?.service || "";
          const isSensitive = SENSITIVE_SERVICES.some((s) => service === s);
          if (isSensitive) {
            applyReadonlyToPanel();
            if (banner) banner.style.display = "flex";
          } else {
            removeReadonlyFromPanel();
            if (banner) banner.style.display = "none";
          }
        } else {
          removeReadonlyFromPanel();
          if (banner) banner.style.display = "none";
        }
      });

      eventBus.on("commandStack.execute", (e: any) => {
        if (canEditRef.current) return;
        const element = e.context?.element || e.context?.shape;
        const service = element?.businessObject?.service || "";
        if (SENSITIVE_SERVICES.some((s) => service === s)) {
          setTimeout(() => {
            try {
              (instance.get("commandStack") as any).undo();
            } catch {}
          }, 0);
        }
      });
    } catch (err: Error | any) {
      container.removeClass("with-diagram").addClass("with-error");
      container.find(".error pre").text(err.message);
      console.error(err);
    }
  }

  async function loadModeling(xml: string): Promise<void> {
    const instance = createModeler();
    attachFormsSync(instance);
    const container = $("#js-drop-zone");
    try {
      await instance.importXML(xml);
      patchScrollAfterImport(instance);
      container.removeClass("with-error").addClass("with-diagram");

      const canvas = instance.get("canvas") as any;
      const elementRegistry = instance.get("elementRegistry") as any;
      const modeling = instance.get("modeling") as any;
      const layouter = instance.get("layouter") as any;
      const definitions = instance.getDefinitions();
      const processEl = findProcessElement(definitions?.rootElements || []);
      const flowElements = processEl?.flowElements || [];

      if (flowElements.length) {
        const flowsForEnrich = flowElements.map((f: any) => ({
          id: f.id || "",
          name: f.name || "",
          $type: f.$type || "",
        }));

        const pseudoProcessData: any = {
          code: processEl?.id || "process",
          label: processEl?.name || processEl?.id || "process",
          nodes: {},
          links: [],
        };
        flowElements.forEach((f: any) => {
          if (
            f.$type === "bpmn:SequenceFlow" ||
            f.$type === "bpmn:StartEvent" ||
            f.$type === "bpmn:EndEvent"
          )
            return;
          const shape = elementRegistry.get(f.id);
          const bo = shape?.businessObject;
          if (!bo || !bo.service) return;

          pseudoProcessData.nodes[f.id] = {
            code: bo.code || f.id,
            label: bo.label || f.name || f.id,
            type: bo.type || "",
            service: bo.service || "",
            params: tryParseJson(bo.params) || {},
            inputs: tryParseJson(bo.inputs) || [],
            outputs: tryParseJson(bo.outputs) || [],
            caseContext: tryParseJson(bo.caseContext) || {},
            initContext: tryParseJson(bo.initContext) || {},
            caseStatus: tryParseJson(bo.caseStatus) || {},
            caseOwner: tryParseJson(bo.caseOwner) || {},
            caseRelationSettings: tryParseJson(bo.caseRelationSettings) || {},
            retry: tryParseJson(bo.retry) || {},
            start: bo.start === "true" || bo.start === true,
            codeTemplate: bo.codeTemplate || "",
            owner: bo.owner || "",
            group: bo.group || "",
            data: bo.data || "",
            startCondition: tryParseJson(bo.startCondition) || {},
            resume: bo.resume === "true" || bo.resume === true,
            resumeCondition: tryParseJson(bo.resumeCondition) || {},
            form: tryParseJson(bo.form) || undefined,
            icon: bo.icon || "",
            dataTemplate: bo.dataTemplate || "",
            dueAtDuration: tryParseJson(bo.dueAtDuration) || {},
            actions: tryParseJson(bo.actions) || {},
            actionParams: tryParseJson(bo.actionParams) || {},
            task: bo.task === "true" || bo.task === true,
          };
        });
        flowElements.forEach((f: any) => {
          if (f.$type === "bpmn:SequenceFlow") {
            const sourceId = f.sourceRef?.id || f.sourceRef;
            const targetId = f.targetRef?.id || f.targetRef;
            if (sourceId && targetId) {
              pseudoProcessData.links.push({
                source: { node: sourceId, output: f.name || "output" },
                target: { node: targetId, input: "input" },
              });
            }
          }
        });

        const enriched = enrichProcessFromXmlNodes(
          pseudoProcessData,
          flowsForEnrich,
        );

        Object.entries(enriched.nodes).forEach(
          ([code, nodeData]: [string, any]) => {
            const shape = elementRegistry.get(code);
            if (!shape) return;
            if (nodeData.type === "startEvent") return;
            if (nodeData.type === "endEvent") {
              addEndEventExtensionProperties(shape, nodeData);
            } else {
              addActivityExtentionProperties(shape, nodeData);
            }
          },
        );
      }
      try {
        const { xml: currentXml } = await instance.saveXML({ format: true });
        if (currentXml) {
          await applyFullLayout(
            elementRegistry,
            modeling,
            canvas,
            layouter,
            {},
            "",
            instance,
          );
        }
      } catch {}
    } catch (err: Error | any) {
      container.removeClass("with-diagram").addClass("with-error");
      container.find(".error pre").text(err.message);
      console.error(err);
    }
  }

  function addNode(
    data: ProcessDefinition,
    code: string,
    io: string,
  ): NodeElement {
    const emptyElement: NodeElement = {
      type: "",
      label: "",
      isIfNode: false,
      output: "",
      node: {} as NodeDefinition,
    };
    const node = data?.nodes?.[code];
    if (!node) return emptyElement;
    const mapping = resolveNodeBpmnMapping(node.type, node.service);
    if (!mapping) return emptyElement;
    return {
      type: mapping.bpmnType,
      label: (node as any).label || node.code || code,
      isIfNode: mapping.isIfNode ?? false,
      output: io,
      node,
    };
  }

  function rebuildFromDefinitions(
    data: ProcessDefinition,
    flowElements: any[],
    definitions: any,
  ): void {
    const nodes: Record<string, any> = {};
    const links: any[] = [];
    const diagramShapes = definitions?.diagrams?.[0]?.plane?.planeElement || [];
    const getPosition = (id: string) => {
      const shape = diagramShapes.find(
        (el: any) => el.id === "BPMNShape_" + id || el.id?.includes(id),
      );
      if (!shape?.bounds) return undefined;
      return {
        x: shape.bounds.x,
        y: shape.bounds.y,
        width: shape.bounds.width || 100,
        height: shape.bounds.height || 80,
      };
    };
    flowElements.forEach((flow: any) => {
      const id = flow.id || "";
      const name = flow.name || "";
      const position = getPosition(id);
      if (flow.$type === "bpmn:StartEvent") {
        nodes[id] = { id, code: id, type: "startEvent", label: name, position };
        if (position)
          (data as any)._startEventPosition = { x: position.x, y: position.y };
      } else if (flow.$type === "bpmn:EndEvent") {
        nodes[id] = { id, code: id, type: "endEvent", label: name, position };
      } else if (
        [
          "bpmn:Task",
          "bpmn:UserTask",
          "bpmn:ServiceTask",
          "bpmn:ScriptTask",
          "bpmn:ManualTask",
          "bpmn:BusinessRuleTask",
          "bpmn:SendTask",
          "bpmn:ReceiveTask",
          "bpmn:ExclusiveGateway",
          "bpmn:ParallelGateway",
          "bpmn:InclusiveGateway",
          "bpmn:CallActivity",
          "bpmn:SubProcess",
          "bpmn:IntermediateCatchEvent",
          "bpmn:IntermediateThrowEvent",
        ].includes(flow.$type)
      ) {
        const nodeType =
          flow.$type === "bpmn:ServiceTask"
            ? NodeType.service
            : NodeType.activity;
        nodes[id] = {
          id,
          code: id,
          type: nodeType,
          service: "",
          label: name,
          params: {},
          start: false,
          initContext: {},
          caseContext: {},
          caseStatus: {},
          caseOwner: {},
          inputs: [],
          outputs: [],
          task: false,
          caseRelationSettings: {},
          retry: {},
          codeTemplate: "",
          owner: "",
          group: "",
          data: {},
          startCondition: {},
          resume: false,
          resumeCondition: {},
          form: {},
          icon: "",
          dataTemplate: "",
          dueAtDuration: {},
          actions: {},
          actionParams: {},
          position,
        };
      } else if (flow.$type === "bpmn:SequenceFlow") {
        const sourceId = flow.sourceRef?.id || flow.sourceRef;
        const targetId = flow.targetRef?.id || flow.targetRef;
        if (sourceId && targetId)
          links.push({
            source: {
              node: sourceId,
              output: normalizeGatewayOutput(flow.name || "output"),
            },
            target: { node: targetId, input: "input" },
          });
      }
    });
    data.nodes = nodes;
    data.links = links;
  }

  function addEndEventExtensionProperties(element: any, nodeData: any) {
    if (!nodeData) return element;
    const bo = element.businessObject;
    bo["code"] = nodeData.code || "";
    bo["label"] = nodeData.label || "";
    bo["service"] = nodeData.service || "";
    bo["type"] = nodeData.type || "endEvent";
    bo["params"] = stringify(nodeData.params);
    bo["caseStatus"] = stringify(nodeData.caseStatus);
    bo["caseContext"] = stringify(nodeData.caseContext);
    bo["inputs"] = stringify(nodeData.inputs);
    return element;
  }

  function addActivityExtentionProperties(element: any, node: NodeDefinition) {
    const businessObject = element.businessObject;
    if (node.type === NodeType.activity) {
      const n = node as ActivityNodeDefinition;
      businessObject["type"] = node.type || "";
      businessObject["service"] = node.service || "";
      businessObject["codeTemplate"] = n?.codeTemplate || "";
      businessObject["owner"] = stringify(n.owner);
      businessObject["group"] = stringify(n.group);
      businessObject["data"] = stringify(n.data);
      businessObject["startCondition"] = stringify(n.startCondition);
      businessObject["caseContext"] = stringify(node.caseContext);
      businessObject["caseOwner"] = stringify(node.caseOwner);
      businessObject["resume"] = n.resume || false;
      businessObject["resumeCondition"] = stringify(n.resumeCondition);
      businessObject["form"] = stringify(n.form);
      businessObject["icon"] = n.icon || "";
      businessObject["start"] = node.start || false;
      businessObject["dataTemplate"] = n.dataTemplate || "";
      businessObject["code"] = node.code || "";
      businessObject["label"] = n.label || "";
      businessObject["dueAtDuration"] = stringify(n.dueAtDuration);
      businessObject["caseRelationSettings"] = stringify(
        node.caseRelationSettings,
      );
      businessObject["actions"] = stringify(n.actions);
      businessObject["actionParams"] = stringify(n.actionParams);
      businessObject["caseStatus"] = stringify(node.caseStatus);
      businessObject["inputs"] = stringify(node.inputs);
      businessObject["outputs"] = stringify(node.outputs);
    } else if (
      node.type === NodeType.simple ||
      node.type === NodeType.service
    ) {
      businessObject["service"] = node.service || "";
      businessObject["type"] = node.type || "";
      businessObject["code"] = node.code || "";
      businessObject["label"] = (node as any).label || "";
      businessObject["start"] = node.start || false;
      businessObject["params"] = stringify(node.params);
      businessObject["inputs"] = stringify(node.inputs);
      businessObject["outputs"] = stringify(node.outputs);
      businessObject["caseContext"] = stringify(node.caseContext);
      businessObject["initContext"] = stringify(node.initContext);
      businessObject["task"] = node.task || false;
      businessObject["caseStatus"] = stringify(node.caseStatus);
      businessObject["caseOwner"] = stringify(node.caseOwner);
      businessObject["caseRelationSettings"] = stringify(
        node.caseRelationSettings,
      );
      businessObject["retry"] = node.retry || {};
    } else if (node.type === NodeType.external) {
      businessObject["code"] = node.code || "";
      businessObject["type"] = node.type || "";
      businessObject["label"] = (node as any).label || "";
    }
    return element;
  }

  function addProcessExtentionProperties(
    element: any,
    process: ProcessDefinition,
  ) {
    const businessObject = element.businessObject;
    businessObject["code"] = process?.code || "";
    businessObject["title"] = process.title || "";
    businessObject["label"] = process.label || process.title || "";
    businessObject["codeTemplate"] = stringify(process.codeTemplate);
    businessObject["actions"] = stringify(process.actions);
    businessObject["activityActions"] = stringify(process.activityActions);
    businessObject["form"] = stringify(process.form);
    businessObject["view"] = stringify(process.view);
    businessObject["filterCategory"] = stringify(process.filterCategory);
    businessObject["filters"] = stringify(process.filters);
    const originalForms = safeParseFormsField(process.forms);
    const serializedReactForms: Record<string, any> = {};
    Object.entries(formsRef.current).forEach(([id, form]) => {
      const f = form as any;
      let schema = f.schema;
      let uischema = f.uischema;
      if (f.fields && (!schema || !uischema)) {
        schema = fieldsToJsonSchema(f.fields);
        uischema = fieldsToUiSchema(f.fields, f.label, f.layout);
      }
      serializedReactForms[id] = {
        id: f.id,
        code: f.code || f.id,
        label: f.label,
        schema,
        uischema,
        scripts: f.scripts || [],
        transformers: f.transformers || [],
        ...f,
      };
    });
    const mergedForms = { ...originalForms, ...serializedReactForms };
    businessObject["forms"] = stringify(mergedForms);
    return element;
  }

  function isTokenValid(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp && payload.exp > now;
    } catch {
      return false;
    }
  }

  async function downloadBPMNJson() {
    try {
      const bpmnJS = modelerRef.current;
      if (!bpmnJS) return;
      await bpmnJS.saveXML({ format: true });
      const definitions = bpmnJS.getDefinitions();
      const json = await defintion2Json(definitions);
      const blob = new Blob([json?.json || ""], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename + ".json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error saving BPMN JSON:", err);
    }
  }

  const newBpmnDiagram = () => {
    try {
      setJsonData(diagramXML);
      loadModeling(diagramXML);
      setError(null);
    } catch (err) {
      setError("Erreur création diagramme");
      setJsonData(null);
    }
  };

  async function downloadBPMNXML() {
    try {
      if (!modelerRef.current) return;
      const { xml } = await modelerRef.current.saveXML({ format: true });
      const blob = new Blob([xml!], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename + ".xml";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error saving BPMN XML:", err);
    }
  }

  async function defintion2Json(
    definitions: ModdleElement,
  ): Promise<SaveJsonResult> {
    try {
      if (!definitions.rootElements?.length)
        return { error: new Error("Aucun rootElement trouvé") };
      const makinaProcess = new MakinaProcess(definitions);
      const processEl = findProcessElement(definitions.rootElements);
      const bpmnProcess = processEl || definitions.rootElements[0];
      const rawId = bpmnProcess?.id || "";
      const isDefaultId = rawId === "Process_1" || rawId === "Definitions_1";
      if (!makinaProcess.code || makinaProcess.code === "Process_1")
        makinaProcess.code = isDefaultId ? "" : rawId;
      if (!makinaProcess.label)
        makinaProcess.label =
          bpmnProcess?.name || makinaProcess.code || rawId || "";
      if (!makinaProcess.code)
        return {
          error: new Error(
            "Le code du processus est vide. Veuillez définir un identifiant (code) dans les propriétés du processus avant de sauvegarder.",
          ),
        };

      addNodesAndLinks(makinaProcess, bpmnProcess.flowElements || []);

      if (bpmnProcess.flowElements?.length) {
        const flowsForEnrich = (bpmnProcess.flowElements as any[]).map(
          (f: any) => ({
            id: f.id || "",
            name: f.name || "",
            $type: f.$type || "",
            sourceRef: f.sourceRef?.id || f.sourceRef || "",
            targetRef: f.targetRef?.id || f.targetRef || "",
          }),
        );

        const nodesNeedServiceEnrich = Object.values(
          makinaProcess.nodes || {},
        ).some(
          (n: any) =>
            n.type !== "startEvent" && n.type !== "endEvent" && !n.service,
        );
        if (nodesNeedServiceEnrich) {
          const enriched = enrichProcessFromXmlNodes(
            makinaProcess as any,
            flowsForEnrich,
          );
          makinaProcess.nodes = enriched.nodes;
        }

        const hasRealFormsInBpmn = (formsObj: any): boolean => {
          if (!formsObj || Object.keys(formsObj).length === 0) return false;
          return Object.values(formsObj).some((f: any) => {
            const props = f?.schema?.properties || {};
            const keys = Object.keys(props);
            return (
              keys.length > 1 ||
              (keys.length === 1 && keys[0] !== "commentaire")
            );
          });
        };

        const existingForms = safeParseFormsField((makinaProcess as any).forms);
        if (
          !hasRealFormsInBpmn(existingForms) &&
          !hasRealFormsInBpmn(formsRef.current)
        ) {
          const generatedForms = buildDefaultForms(makinaProcess.nodes || {});
          (makinaProcess as any).forms = {
            ...generatedForms,
            ...existingForms,
          };
        }

        const existingFilterCat = (makinaProcess as any).filterCategory;
        const filterCatEmpty =
          !existingFilterCat ||
          (Array.isArray(existingFilterCat)
            ? existingFilterCat.length === 0
            : Object.keys(existingFilterCat).length === 0);
        const existingFilters = (makinaProcess as any).filters;
        const filtersEmpty =
          !existingFilters ||
          (Array.isArray(existingFilters)
            ? existingFilters.length === 0
            : Object.keys(existingFilters).length === 0);
        if (filterCatEmpty || filtersEmpty) {
          const processCode = makinaProcess.code || "process";
          const { filterCategory, filters } = buildDefaultFiltersAndCategory(
            processCode,
            makinaProcess.nodes || {},
            makinaProcess.label,
          );
          if (filterCatEmpty)
            (makinaProcess as any).filterCategory = filterCategory;
          if (filtersEmpty) (makinaProcess as any).filters = filters;
        }

        const existingActions = (makinaProcess as any).actions;
        if (!existingActions || Object.keys(existingActions).length === 0) {
          (makinaProcess as any).actions = {
            owner: { comment: false },
            createdBy: { comment: false, "self-assign": true },
            roles: {
              R_READ_ALL: { comment: false },
              R_WRITE_ALL: { comment: false },
            },
            groups: {
              R_ACTIVITIES: { comment: false, "self-assign": true },
              GROUP_ADMIN: { comment: true },
            },
          };
        }
        const existingActivityActions = (makinaProcess as any).activityActions;
        if (
          !existingActivityActions ||
          Object.keys(existingActivityActions).length === 0
        ) {
          (makinaProcess as any).activityActions = {
            owner: { comment: false },
            group: { comment: false },
            caseOwner: { comment: false },
            caseCreatedBy: { comment: false },
            roles: {
              R_READ_ALL: { comment: false },
              R_WRITE_ALL: { comment: false },
            },
            groups: {
              R_ACTIVITIES: { comment: false },
              GROUP_ADMIN: { comment: true },
            },
          };
        }
      }

      const processJsonFields = [
        "actions",
        "activityActions",
        "form",
        "view",
        "filterCategory",
        "filters",
        "codeTemplate",
        "actionParams",
      ];
      for (const field of processJsonFields) {
        const val = (makinaProcess as any)[field];
        if (typeof val === "string" && val !== "") {
          try {
            (makinaProcess as any)[field] = JSON.parse(val);
          } catch {}
        }
      }

      const CASESTATUSES: Record<string, string> = {
        soumettre: "En-cours-de-traitement",
        submit: "En-cours-de-traitement",
        demande: "En-cours-de-traitement",
        saisir: "En-cours-de-traitement",
        creer: "En-cours-de-traitement",
        verifier: "En-cours-de-verification",
        verify: "En-cours-de-verification",
        valider: "En-cours-de-verification",
        validation: "En-cours-de-verification",
        demander: "Documents-manquants",
        request: "Documents-manquants",
        fournir: "Documents-fournis",
        provide: "Documents-fournis",
        enregistrer: "Enregistree",
        register: "Enregistree",
        commande: "En-cours-de-commande",
        order: "En-cours-de-commande",
        passer: "En-cours-de-commande",
        analyser: "En-cours-d-analyse",
        analyze: "En-cours-d-analyse",
        risk: "En-cours-d-analyse",
        decision: "En-cours-d-approbation",
        preparer: "Offre-en-preparation",
        prepare: "Offre-en-preparation",
        signer: "En-attente-de-signature",
        sign: "En-attente-de-signature",
        confirm: "Fonds-recus",
        reception: "Fonds-recus",
        receptionner: "Fonds-recus",
        recevoir: "Fonds-recus",
      };
      const ACTIONLABELS: Record<string, string> = {
        soumettre: "Soumettre la demande",
        submit: "Soumettre la demande",
        demande: "Soumettre la demande",
        saisir: "Soumettre la demande",
        creer: "Créer la demande",
        verifier: "Valider la vérification",
        verify: "Valider la vérification",
        valider: "Valider",
        validation: "Valider",
        demander: "Envoyer la demande",
        request: "Envoyer la demande",
        fournir: "Transmettre les documents",
        provide: "Transmettre les documents",
        commande: "Passer la commande",
        order: "Passer la commande",
        passer: "Passer la commande",
        analyser: "Soumettre l'analyse",
        analyze: "Soumettre l'analyse",
        risk: "Soumettre l'analyse",
        decision: "Confirmer la décision",
        preparer: "Valider l'offre",
        prepare: "Valider l'offre",
        signer: "Signer le contrat",
        sign: "Signer le contrat",
        confirm: "Confirmer la réception",
        reception: "Confirmer la réception",
        receptionner: "Confirmer la réception",
        recevoir: "Confirmer la réception",
      };
      const nodes = makinaProcess.nodes || {};
      Object.entries(nodes).forEach(([code, node]: [string, any]) => {
        if (node.type !== NodeType.activity) return;
        const lc = code.toLowerCase().replace(/[_\-]/g, "");
        const labelLc = (node.label || "").toLowerCase();
        if (!node.actionParams || Object.keys(node.actionParams).length === 0) {
          const actionLabel =
            Object.entries(ACTIONLABELS).find(
              ([k]) => lc.includes(k) || labelLc.includes(k),
            )?.[1] || `Valider — ${node.label}`;
          node.actionParams = { submit: { label: actionLabel } };
        }
        if (!node.caseStatus || Object.keys(node.caseStatus).length === 0) {
          const status = Object.entries(CASESTATUSES).find(
            ([k]) => lc.includes(k) || labelLc.includes(k),
          )?.[1];
          if (status) node.caseStatus = status;
        }
      });

      const rawFormsFromBpmn = (makinaProcess as any)["forms"];
      const parsedFormsFromBpmn = safeParseFormsField(rawFormsFromBpmn);
      const isRealForm = (f: any) => {
        const props = f?.schema?.properties || {};
        const keys = Object.keys(props);
        return (
          keys.length > 1 || (keys.length === 1 && keys[0] !== "commentaire")
        );
      };
      const mergedForms: Record<string, any> = { ...parsedFormsFromBpmn };
      Object.entries(formsRef.current).forEach(([k, v]) => {
        if (isRealForm(v) || !isRealForm(mergedForms[k])) {
          mergedForms[k] = v;
        }
      });
      (makinaProcess as any)["forms"] = mergedForms;

      if (!makinaProcess.codeTemplate) {
        const processCode = makinaProcess.code || "process";
        (makinaProcess as any).codeTemplate =
          `${processCode.toUpperCase().slice(0, 4)}-{{_rand}}-{{_count[8]}}`;
      }

      if (!makinaProcess.title) {
        const firstStartNode = Object.values(nodes).find(
          (n: any) => n.start === true && n.type === "activity",
        ) as any;
        if (firstStartNode?.code) {
          const cc = firstStartNode.code;
          (makinaProcess as any).title =
            `{{context.node.${cc}.user.fullname}} / {{context.node.${cc}.data.description || ""}}`;
        } else {
          (makinaProcess as any).title = `{{context.createdAt}}`;
        }
      }

      const existingSearchFields = (makinaProcess as any).searchFields;
      if (
        !existingSearchFields ||
        !Array.isArray(existingSearchFields) ||
        existingSearchFields.length === 0
      ) {
        const sfActivity: any[] = [];
        const sfCase: any = {
          type: "case",
          fields: [
            {
              name: "status",
              label: "Statut",
              inputType: "text",
              compact: true,
            },
            { name: "code", label: "Code", inputType: "text" },
            {
              name: "createdBy",
              label: "Créé par",
              inputType: "text",
              compact: true,
              compactTitle: true,
            },
            { name: "owner", label: "Propriétaire", inputType: "text" },
            { name: "createdAt", label: "Date de création", inputType: "date" },
            {
              name: "updatedAt",
              label: "Date de mise à jour",
              inputType: "date",
              compact: true,
            },
          ],
        };
        for (const [nodeCode, node] of Object.entries(nodes) as [
          string,
          any,
        ][]) {
          if (node.type !== "activity") continue;
          const formRef = node.form;
          const formCode =
            typeof formRef === "string"
              ? formRef
              : Array.isArray(formRef) && formRef.length > 0
                ? formRef[0]?.code || formRef[0]
                : null;
          if (!formCode) continue;
          const formDef = mergedForms[formCode] || formsRef.current[formCode];
          if (!formDef?.schema?.properties) continue;
          const fields = Object.entries(formDef.schema.properties).map(
            ([fieldName, fieldDef]: [string, any]) => ({
              name: `${formCode}.${fieldName}`,
              label: fieldDef.title || fieldName,
              inputType:
                fieldDef.type === "number"
                  ? "number"
                  : fieldDef.type === "boolean"
                    ? "text"
                    : "text",
              isJson: true,
              compact: sfActivity.length === 0,
              ...(sfActivity.length === 0 ? { compactTitle: true } : {}),
            }),
          );
          if (fields.length > 0) {
            sfActivity.push({
              type: "activity",
              label: node.label || nodeCode,
              code: nodeCode,
              fields: fields.slice(0, 6),
            });
          }
        }
        if (sfActivity.length > 0 || sfCase.fields.length > 0) {
          (makinaProcess as any).searchFields = [...sfActivity, sfCase];
        }
      }

      for (const [nodeCode, node] of Object.entries(nodes) as [string, any][]) {
        if (node.type !== "activity" || !node.start) continue;
        const ctx = node.caseContext || {};
        const hasOnlyRawKey =
          Object.keys(ctx).length === 1 && ctx[nodeCode] === "{{data}}";
        if (hasOnlyRawKey) {
          node.caseContext = {
            [nodeCode]: "{{data}}",
            client: {
              fullname: "{{user.fullname}}",
              email: "{{user.email}}",
            },
          };
        }
      }

      stripEmptyOptionalFields(makinaProcess.nodes || {});
      filename = makinaProcess.code || "diagram";
      const finalProcess = formatJson(makinaProcess);
      const json = JSON.stringify(finalProcess, null, 2);
      return { json };
    } catch (err) {
      console.error("Error saving BPMN JSON:", err);
    }
    return { error: new Error("Error converting to JSON") };
  }

  function sendToWebADVANCED_PROCESS_MANAGEMENT(processData: any) {
    const currentIsCreateMode =
      new URLSearchParams(window.location.search).get("mode") === "create";
    const target = window.parent !== window ? window.parent : window.opener;
    if (target && target !== window) {
      const messageType = currentIsCreateMode
        ? "CREATE_PROCESS"
        : "SAVE_PROCESS";
      target.postMessage({ type: messageType, payload: processData }, "*");
      setSaveNotification({
        open: true,
        message: "Diagramme envoyé !",
        type: "success",
      });
      return;
    }
    setSaveNotification({
      open: true,
      message: "Impossible de contacter webADVANCED_PROCESS_MANAGEMENT",
      type: "error",
    });
  }

  async function saveBPMNToADVANCED_PROCESS_MANAGEMENT() {
    try {
      const bpmnJS = modelerRef.current;
      if (!bpmnJS) return;
      //await bpmnJS.saveXML({ format: true });
      const { xml } = await bpmnJS.saveXML({ format: true });
      const definitions = bpmnJS.getDefinitions();
      const json = await defintion2Json(definitions);
      if (!json?.json) {
        const msg = json?.error?.message || "Aucun JSON généré";
        setSaveNotification({ open: true, message: msg, type: "error" });
        return;
      }
      const processData = JSON.parse(json.json);
      processData.xml = xml;
      if (typeof processData.filterCategory === "string") {
        try {
          processData.filterCategory = JSON.parse(processData.filterCategory);
        } catch {
          processData.filterCategory = [];
        }
      }
      if (typeof processData.filters === "string") {
        try {
          processData.filters = JSON.parse(processData.filters);
        } catch {
          processData.filters = [];
        }
      }
      const hasFilters =
        processData.filters &&
        (Array.isArray(processData.filters)
          ? processData.filters.length > 0
          : Object.keys(processData.filters).length > 0);
      const hasFilterCategory =
        processData.filterCategory &&
        (Array.isArray(processData.filterCategory)
          ? processData.filterCategory.length > 0
          : Object.keys(processData.filterCategory).length > 0);
      if (!hasFilters || !hasFilterCategory) {
        const processCode = processData.code || "process-default";
        const processCode2 = processData.code || "process-default";
        if (!hasFilterCategory || !hasFilters) {
          const { filterCategory: fc2, filters: fl2 } =
            buildDefaultFiltersAndCategory(
              processCode2,
              processData.nodes || {},
              processData.label,
            );
          if (!hasFilterCategory) processData.filterCategory = fc2;
          if (!hasFilters) processData.filters = fl2;
        }
        setPendingProcessData(processData);
        setShowFilterWarning(true);
        return;
      }
      processData.forms = safeParseFormsField(processData.forms);
      if (Object.keys(formsRef.current).length > 0)
        processData.forms = { ...processData.forms, ...formsRef.current };
      if (!processData.forms) processData.forms = {};
      if (!processData.form) processData.form = [];
      if (!processData.view) processData.view = {};
      if (!processData.actions) processData.actions = {};
      if (!processData.activityActions) processData.activityActions = {};
      if (!processData.priorities) processData.priorities = [];
      if (!processData.title)
        processData.title = processData.label || processData.code;
      const securityError = validateNodesForSecurity(processData.nodes);
      if (securityError) {
        setSaveNotification({
          open: true,
          message: `⛔ ${securityError}`,
          type: "error",
        });
        return;
      }
      sendToWebADVANCED_PROCESS_MANAGEMENT(processData);
    } catch (err) {
      setSaveNotification({
        open: true,
        message: "Erreur lors de la sauvegarde",
        type: "error",
      });
    }
  }

  function parseNodeFields(raw: any): any {
    const jsonFields = [
      "params",
      "inputs",
      "outputs",
      "caseContext",
      "caseOwner",
      "caseStatus",
      "caseRelationSettings",
      "initContext",
      "startCondition",
      "resumeCondition",
      "dueAtDuration",
      "actions",
      "actionParams",
      "form",
      "retry",
    ];
    const result = { ...raw };
    for (const field of jsonFields) {
      if (typeof result[field] === "string" && result[field] !== "") {
        try {
          result[field] = JSON.parse(result[field]);
        } catch {}
      }
    }
    return result;
  }
  function normalizeGatewayOutput(raw: string): string {
    if (!raw || raw === "output") return raw;
    const lower = raw.toLowerCase().trim();
    if (
      lower === "yes" ||
      lower === "oui" ||
      lower.includes("approuv") ||
      lower.includes("valid") ||
      lower.includes("accept") ||
      lower.includes("conforme") ||
      lower.includes("ok")
    )
      return "yes";
    if (
      lower === "no" ||
      lower === "non" ||
      lower.includes("rejet") ||
      lower.includes("refus") ||
      lower.includes("non") ||
      lower.includes("ko") ||
      lower.includes("invalid")
    )
      return "no";
    return raw;
  }
  function addNodesAndLinks(
    makinaProcess: MakinaProcess,
    flows: ModdleElement[],
  ) {
    const nodes: Record<string, any> = {};
    const links: any[] = [];
    const elementRegistry = modelerRef.current?.get("elementRegistry") as any;
    if (!elementRegistry) return;

    flows.forEach((flow) => {
      if (
        [
          "bpmn:Task",
          "bpmn:UserTask",
          "bpmn:ServiceTask",
          "bpmn:ScriptTask",
          "bpmn:ManualTask",
          "bpmn:BusinessRuleTask",
          "bpmn:SendTask",
          "bpmn:ReceiveTask",
          "bpmn:ExclusiveGateway",
          "bpmn:ParallelGateway",
          "bpmn:InclusiveGateway",
          "bpmn:CallActivity",
          "bpmn:SubProcess",
          "bpmn:IntermediateCatchEvent",
          "bpmn:IntermediateThrowEvent",
        ].includes(flow.$type)
      ) {
        const node = new ActivityNodeDefinition(flow);
        const nodeCode = node.code || flow.id || "";
        if (!nodeCode) {
          console.warn("Nœud sans code ni id, ignoré :", flow);
          return;
        }
        node.code = nodeCode;
        const nodeLabel = flow.name || node.label || nodeCode;
        node.label = nodeLabel;
        const shape = elementRegistry.get(flow.id);
        const position = shape
          ? {
              x: Math.round(shape.x),
              y: Math.round(shape.y),
              width: Math.round(shape.width),
              height: Math.round(shape.height),
            }
          : undefined;
        nodes[nodeCode] = formatJson(
          parseNodeFields({ ...node, label: nodeLabel, position }),
        );
      }
      if (flow.$type === "bpmn:StartEvent") {
        const startId = flow.id || "start_event_1";
        const shape = elementRegistry.get(flow.id);
        const position = shape
          ? {
              x: Math.round(shape.x),
              y: Math.round(shape.y),
              width: Math.round(shape.width),
              height: Math.round(shape.height),
            }
          : undefined;
        if (position)
          (makinaProcess as any)._startEventPosition = {
            x: position.x,
            y: position.y,
          };
        nodes[startId] = formatJson({
          id: startId,
          code: startId,
          type: "startEvent",
          label: flow.name || "Début",
          position,
        });
      }
      if (flow.$type === "bpmn:EndEvent") {
        const endId = flow.id || "end_event_1";
        const bo = flow as any;
        const nodeKey = bo.code || endId;
        const shape = elementRegistry.get(flow.id);
        const position = shape
          ? {
              x: Math.round(shape.x),
              y: Math.round(shape.y),
              width: Math.round(shape.width),
              height: Math.round(shape.height),
            }
          : undefined;
        nodes[endId] = formatJson({
          id: endId,
          code: bo.code || endId,
          type: bo.type || "endEvent",
          service: bo.service || "",
          label: flow.name || bo.label || "Fin",
          params: tryParseJson(bo.params) || {},
          caseStatus: tryParseJson(bo.caseStatus) || {},
          caseContext: tryParseJson(bo.caseContext) || {},
          inputs: tryParseJson(bo.inputs) || [{ label: "", code: "input" }],
          outputs: [],
          position,
        });
      }
      if (flow.$type === "bpmn:SequenceFlow") {
        if (!flow.sourceRef || !flow.targetRef) return;
        const sourceCode = resolveNodeCode(flow.sourceRef);
        const targetCode = resolveNodeCode(flow.targetRef);
        if (!sourceCode || !targetCode) {
          console.warn(
            "SequenceFlow : source ou target introuvable :",
            flow.id,
          );
          return;
        }
        links.push(
          formatJson({
            source: { node: sourceCode, output: flow.name || "output" },
            target: { node: targetCode, input: "input" },
          }),
        );
      }
    });

    makinaProcess.nodes = nodes;
    makinaProcess.links = links;
  }

  const parseFormsFromPrompt = (
    userPrompt: string,
    activityNodes: { code: string; label: string }[],
  ): Record<string, any> => {
    const forms: Record<string, any> = {};

    const makeUiSchema = (
      label: string,
      fields: { code: string; label: string; type: string }[],
    ) => {
      const rows: any[] = [];
      for (let i = 0; i < fields.length; i += 2) {
        const row = fields.slice(i, i + 2);
        const gridSize = row.length === 1 ? 12 : 6;
        rows.push({
          type: "HorizontalLayout",
          elements: row.map((f) => {
            const control: any = {
              type: "Control",
              label: f.label,
              scope: `#/properties/${f.code}`,
              options: { grid: { md: gridSize, sm: gridSize, xs: 12 } },
            };
            if (f.type === "number") {
              control.options = buildNumberControlOptions(
                gridSize,
                gridSize,
                12,
              );
            }
            return control;
          }),
        });
      }
      return {
        type: "Categorization",
        elements: [
          {
            type: "Category",
            label,
            elements: [
              { type: "Group", options: { variant: "paper" }, elements: rows },
            ],
          },
        ],
      };
    };

    const promptLower = userPrompt.toLowerCase();

    const extractFieldsSection = (
      sectionKeywords: string[],
      nextSectionKeywords: string[],
    ): string[] => {
      let startIdx = -1;
      for (const kw of sectionKeywords) {
        const idx = promptLower.indexOf(kw.toLowerCase());
        if (idx !== -1 && (startIdx === -1 || idx < startIdx)) startIdx = idx;
      }
      if (startIdx === -1) return [];

      let endIdx = userPrompt.length;
      for (const kw of nextSectionKeywords) {
        const idx = promptLower.indexOf(kw.toLowerCase(), startIdx + 10);
        if (idx !== -1 && idx < endIdx) endIdx = idx;
      }

      const section = userPrompt.slice(startIdx, endIdx);
      const fields: string[] = [];
      const lines = section.split("\n");
      for (const line of lines) {
        const trimmed = line.trim();
        if (
          trimmed.startsWith("*") ||
          trimmed.startsWith("-") ||
          trimmed.startsWith("•")
        ) {
          const field = trimmed.replace(/^[*\-•]\s*/, "").trim();
          if (
            field &&
            field.length < 60 &&
            !field.includes(":") &&
            !field.toLowerCase().includes("formulaire") &&
            !field.toLowerCase().includes("tâche") &&
            !field.toLowerCase().includes("user task") &&
            !field.toLowerCase().includes("soumettre") &&
            !field.toLowerCase().includes("valider") &&
            !field.toLowerCase().includes("créer") &&
            !field.toLowerCase().includes("générer")
          ) {
            const fieldCode = field
              .split(/[\s(]/)[0]
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .replace(/[^a-zA-Z0-9_]/g, "")
              .replace(/^[0-9]/, "_");
            if (fieldCode.length >= 2) {
              fields.push(fieldCode + "|" + field.split("(")[0].trim());
            }
          }
        }
      }
      return fields;
    };

    const TASK_SECTION_KEYWORDS: Record<
      string,
      { keywords: string[]; nextKeywords: string[] }
    > = {
      soumettre: {
        keywords: ["soumettre", "demande de cr", "submit"],
        nextKeywords: ["vérifier", "verifier", "conseiller"],
      },
      verifier: {
        keywords: ["vérifier", "verifier", "complétude", "completude"],
        nextKeywords: ["passerelle", "dossier complet", "si non", "si oui"],
      },
      demander_docs: {
        keywords: ["demander les documents", "documents manquants"],
        nextKeywords: ["fournir", "client\nuser task"],
      },
      fournir_docs: {
        keywords: [
          "fournir les documents",
          "ajout de documents",
          "uploadDocuments",
        ],
        nextKeywords: ["retour", "enregistrer", "système bancaire"],
      },
      analyser: {
        keywords: ["analyser le risque", "analyse de risque"],
        nextKeywords: ["passerelle", "risque acceptable", "si non"],
      },
      decision: {
        keywords: [
          "décision crédit",
          "decision credit",
          "validation crédit",
          "validation credit",
        ],
        nextKeywords: [
          "passerelle",
          "crédit approuvé",
          "credit approuve",
          "si non",
        ],
      },
      preparer: {
        keywords: ["préparer l'offre", "preparer l'offre", "offre de crédit"],
        nextKeywords: ["client\nuser task", "consulter", "signer"],
      },
      signer: {
        keywords: [
          "consulter et signer",
          "signature du contrat",
          "signer l'offre",
        ],
        nextKeywords: [
          "passerelle",
          "contrat signé",
          "contrat signe",
          "si non",
        ],
      },
      confirmation: {
        keywords: [
          "confirmation",
          "réception des fonds",
          "reception des fonds",
        ],
        nextKeywords: ["fin (crédit", "fin (credit", "fin\n"],
      },
    };

    const nodeKeywordMap: Record<string, string> = {
      soumettre: "soumettre",
      submit: "soumettre",
      demandecredit: "soumettre",
      soumettreDemande: "soumettre",
      verifier: "verifier",
      verifierdossier: "verifier",
      verifydossier: "verifier",
      demanderdocs: "demander_docs",
      askdocs: "demander_docs",
      requestdocs: "demander_docs",
      fourniirdocs: "fournir_docs",
      fourniirdocuments: "fournir_docs",
      providedocs: "fournir_docs",
      fournirdocs: "fournir_docs",
      analysRisque: "analyser",
      analyserrisque: "analyser",
      analyzerisk: "analyser",
      riskanalysis: "analyser",
      decisioncredit: "decision",
      creditdecision: "decision",
      decisioncredit2: "decision",
      prepareroffre: "preparer",
      prepareoffer: "preparer",
      prepoffer: "preparer",
      consultersigner: "signer",
      signoffer: "signer",
      signoffre: "signer",
      confirmationreception: "confirmation",
      confirmfunds: "confirmation",
      receivefunds: "confirmation",
    };

    for (const node of activityNodes) {
      const formCode = `formulaire${node.code.charAt(0).toUpperCase() + node.code.slice(1)}`;
      const normalizedCode = node.code.toLowerCase().replace(/[_\-]/g, "");
      const labelLower = node.label.toLowerCase();

      let sectionKey = nodeKeywordMap[normalizedCode];
      if (!sectionKey) {
        if (
          labelLower.includes("soumettre") ||
          labelLower.includes("demande de cr")
        )
          sectionKey = "soumettre";
        else if (
          labelLower.includes("vérif") ||
          labelLower.includes("verif") ||
          labelLower.includes("complet")
        )
          sectionKey = "verifier";
        else if (
          labelLower.includes("document") &&
          (labelLower.includes("demand") || labelLower.includes("manquant"))
        )
          sectionKey = "demander_docs";
        else if (
          labelLower.includes("document") &&
          (labelLower.includes("fournir") || labelLower.includes("transm"))
        )
          sectionKey = "fournir_docs";
        else if (labelLower.includes("risque") || labelLower.includes("analys"))
          sectionKey = "analyser";
        else if (
          labelLower.includes("décision") ||
          labelLower.includes("decision") ||
          (labelLower.includes("crédit") && labelLower.includes("valid"))
        )
          sectionKey = "decision";
        else if (labelLower.includes("offre") || labelLower.includes("prepar"))
          sectionKey = "preparer";
        else if (labelLower.includes("sign") || labelLower.includes("contrat"))
          sectionKey = "signer";
        else if (
          labelLower.includes("confirm") ||
          labelLower.includes("réception") ||
          labelLower.includes("reception")
        )
          sectionKey = "confirmation";
      }

      let rawFields: string[] = [];
      if (sectionKey && TASK_SECTION_KEYWORDS[sectionKey]) {
        const { keywords, nextKeywords } = TASK_SECTION_KEYWORDS[sectionKey];
        rawFields = extractFieldsSection(keywords, nextKeywords);
      }

      const fields: { code: string; label: string; type: string }[] =
        rawFields.map((f) => {
          const [code, label] = f.split("|");
          const lc = code.toLowerCase();
          const type = lc.includes("date")
            ? "string"
            : lc.includes("upload") || lc.includes("pdf")
              ? "string"
              : lc.includes("complet") ||
                  lc.includes("signe") ||
                  lc.includes("solvabilite") ||
                  lc.includes("acceptable") ||
                  lc.includes("recu") ||
                  lc.includes("approuve")
                ? "boolean"
                : lc.includes("montant") ||
                    lc.includes("taux") ||
                    lc.includes("duree") ||
                    lc.includes("score") ||
                    lc.includes("mensualite") ||
                    lc.includes("revenu")
                  ? "number"
                  : "string";
          return { code, label: label || code, type };
        });

      if (fields.length === 0) {
        const nodesForFallback: Record<string, any> = {
          [node.code]: { type: "activity", label: node.label },
        };
        const fallback = buildDefaultForms(nodesForFallback);
        if (fallback[formCode]) {
          forms[formCode] = fallback[formCode];
          continue;
        }
      }

      const properties: Record<string, any> = {};
      fields.forEach(({ code, label, type }) => {
        properties[code] = { type, title: label };
      });

      forms[formCode] = {
        id: formCode,
        code: formCode,
        label: node.label,
        schema: { type: "object", properties, required: [] },
        uischema: makeUiSchema(node.label, fields),
      };
    }
    return forms;
  };

  const generateFormsFromPrompt = async (
    userPrompt: string,
    activityNodes: { code: string; label: string }[],
  ): Promise<Record<string, any>> => {
    if (!activityNodes.length) return {};

    const parsedForms = parseFormsFromPrompt(userPrompt, activityNodes);

    const tasksList = activityNodes
      .map((n) => `- code: "${n.code}", label: "${n.label}"`)
      .join("\n");

    const tasksExamples = activityNodes
      .slice(0, 2)
      .map(
        (n) =>
          `"formulaire${n.code.charAt(0).toUpperCase() + n.code.slice(1)}"`,
      )
      .join(", ");

    const formsPrompt =
      "Tu es un expert en conception de formulaires métier. " +
      "Pour le processus suivant, génère les formulaires JSON pour chaque tâche utilisateur.\n\n" +
      "Description du processus :\n" +
      userPrompt +
      "\n\nListe des tâches (code exact → label) :\n" +
      tasksList +
      "\n\nRègles ABSOLUES :\n" +
      "- Retourne UNIQUEMENT un objet JSON valide, sans markdown, sans backticks, sans explications\n" +
      "- La clé de chaque entrée est EXACTEMENT 'formulaire' + première lettre du code en MAJUSCULE + reste du code\n" +
      `  Exemples : ${tasksExamples}\n` +
      "- Chaque valeur : { id, code, label, schema: { type:'object', properties: { <champ>: { type: 'string'|'number'|'boolean', title:'...'} }, required:[] }, uischema }\n" +
      "- Utilise EXACTEMENT les champs listés dans le processus pour chaque tâche\n" +
      "- Format : objet JSON pur commençant par { et finissant par }";

    try {
      const raw = await callBackendAi(formsPrompt);
      if (raw) {
        const clean = raw
          .replace(/```json\s*/gi, "")
          .replace(/```\s*/g, "")
          .trim();
        const parsed = JSON.parse(clean);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          const normalized: Record<string, any> = {};
          for (const [key, form] of Object.entries(parsed) as [string, any][]) {
            const props = form?.schema?.properties || {};
            const fields = Object.entries(props).map(
              ([k, v]: [string, any]) => ({
                code: k,
                label: (v as any).title || k,
                type: (v as any).type || "string",
              }),
            );
            const rows: any[] = [];
            for (let i = 0; i < fields.length; i += 2) {
              const row = fields.slice(i, i + 2);
              const gridSize = row.length === 1 ? 12 : 6;
              rows.push({
                type: "HorizontalLayout",
                elements: row.map((f) => {
                  const control: any = {
                    type: "Control",
                    label: f.label,
                    scope: `#/properties/${f.code}`,
                    options: { grid: { md: gridSize, sm: gridSize, xs: 12 } },
                  };
                  if (f.type === "number") {
                    control.options = buildNumberControlOptions(
                      gridSize,
                      gridSize,
                      12,
                    );
                  }
                  return control;
                }),
              });
            }
            normalized[key] = {
              ...form,
              id: key,
              code: key,
              uischema: {
                type: "Categorization",
                elements: [
                  {
                    type: "Category",
                    label: form.label || key,
                    elements: [
                      {
                        type: "Group",
                        options: { variant: "paper" },
                        elements: rows,
                      },
                    ],
                  },
                ],
              },
            };
          }
          const hasRealFields = Object.values(normalized).some((f: any) => {
            const keys = Object.keys(f?.schema?.properties || {});
            return (
              keys.length > 1 ||
              (keys.length === 1 && keys[0] !== "commentaire")
            );
          });
          if (hasRealFields) return normalized;
        }
      }
    } catch {}

    return parsedForms;
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt) return;
    aiPromptRef.current = aiPrompt;
    setIsAiLoading(true);
    setError(null);
    const handleXmlResult = async (text: string): Promise<boolean> => {
      const cleanedXml = cleanAndValidateXml(text);
      if (!cleanedXml) return false;
      setJsonData(cleanedXml);
      setError(null);
      await loadModeling(cleanedXml);
      setIsAiLoading(false);

      const m = modelerRef.current as any;
      if (!m) return true;

      const elementRegistry = m.get("elementRegistry") as any;
      const activityNodes: { code: string; label: string }[] = [];
      elementRegistry.getAll().forEach((el: any) => {
        const bo = el.businessObject;
        if (!bo) return;
        const bpmnType = bo.$type || "";
        if (bpmnType === "bpmn:UserTask" || bpmnType === "bpmn:Task") {
          const code = bo.code || bo.id || "";
          const label = bo.name || code;
          if (code) activityNodes.push({ code, label });
        }
      });

      if (activityNodes.length === 0) return true;

      const applyForms = (generatedForms: Record<string, any>) => {
        if (Object.keys(generatedForms).length === 0) return;

        const normalizeForm = (form: any): DynamicFormSchemaLocal => {
          let fields: FormField[] = [];
          if (Array.isArray(form.fields) && form.fields.length > 0) {
            fields = form.fields;
          } else if (
            form.schema?.properties &&
            Object.keys(form.schema.properties).length > 0
          ) {
            fields = schemaToFields(form.schema, form.uischema);
          }
          return {
            ...form,
            id: form.id || form.code || "",
            label: form.label || form.id || "",
            fields,
            updatedAt: form.updatedAt || new Date().toISOString(),
          } as DynamicFormSchemaLocal;
        };

        const normalizedForms: Record<string, DynamicFormSchemaLocal> = {};
        Object.entries(generatedForms).forEach(([k, v]) => {
          normalizedForms[k] = normalizeForm(v);
        });

        const merged: Record<string, DynamicFormSchemaLocal> = {
          ...formsRef.current,
          ...normalizedForms,
        };
        formsRef.current = merged;

        setTimeout(() => {
          setFormsState((prev) => ({ ...prev, ...normalizedForms }));
        }, 200);

        try {
          const canvas = m.get("canvas");
          const modeling = m.get("modeling");
          const root = canvas.getRootElement();
          const formsJson = JSON.stringify(merged);
          modeling.updateProperties(root, { forms: formsJson });
          const allEls = elementRegistry.getAll();
          const processShape = allEls.find(
            (el: any) => el.businessObject?.$type === "bpmn:Process",
          );
          if (processShape) {
            modeling.updateProperties(processShape, { forms: formsJson });
          }
          activityNodes.forEach(({ code }) => {
            const candidates = [
              `formulaire${code.charAt(0).toUpperCase() + code.slice(1)}`,
              `formulaire${code}`,
              `formulaire_${code}`,
            ];
            const formCode =
              candidates.find((c) => generatedForms[c]) || candidates[0];
            if (!generatedForms[formCode]) return;
            const shape = allEls.find((el: any) => {
              const bo = el.businessObject;
              return bo && (bo.code === code || bo.id === code);
            });
            if (shape) {
              modeling.updateProperties(shape, { form: formCode });
            }
          });
        } catch {}
      };

      const promptToUse = aiPromptRef.current || aiPrompt;
      let generatedForms: Record<string, any> = {};

      try {
        generatedForms = await generateFormsFromPrompt(
          promptToUse,
          activityNodes,
        );
      } catch {}

      const hasRealFields = Object.values(generatedForms).some((f: any) => {
        const keys = Object.keys(f?.schema?.properties || {});
        return (
          keys.length > 1 || (keys.length === 1 && keys[0] !== "commentaire")
        );
      });

      if (!hasRealFields) {
        const nodesForForms: Record<string, any> = {};
        activityNodes.forEach(({ code, label }) => {
          nodesForForms[code] = { type: "activity", label };
        });
        generatedForms = buildDefaultForms(nodesForForms);
      }

      applyForms(generatedForms);
      return true;
    };
    const bpmnPrompt =
      "Tu es un expert BPMN 2.0. Genere UNIQUEMENT le XML BPMN 2.0 valide pour ce processus :\n" +
      aiPrompt +
      "\n\nRegles ABSOLUES :\n" +
      "- Retourne UNIQUEMENT le XML brut, sans markdown, sans backticks, sans explications\n" +
      '- Le XML commence obligatoirement par : <?xml version="1.0" encoding="UTF-8"?>\n' +
      '- Namespaces obligatoires : xmlns:bpmn2="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"\n' +
      "- Termine obligatoirement par </bpmn2:definitions>\n" +
      "- Chaque bpmndi:BPMNShape DOIT avoir bpmnElement referencant un id existant dans le Process\n" +
      "- Chaque bpmndi:BPMNEdge DOIT avoir bpmnElement referencant un SequenceFlow existant\n" +
      "- Chaque SequenceFlow DOIT avoir sourceRef et targetRef valides\n" +
      "- Tous les tags doivent etre correctement fermes\n" +
      '- Le BPMNPlane doit avoir bpmnElement="[id du Process]"\n' +
      "- Utilise des ids courts et valides (ex: task_1, flow_1, start_1)\n" +
      "- Utilise bpmn2:UserTask pour les tâches humaines, bpmn2:ExclusiveGateway pour les décisions, bpmn2:ServiceTask pour les services automatiques, bpmn2:ScriptTask pour les scripts";

    try {
      const text = await callBackendAi(bpmnPrompt);
      const ok = await handleXmlResult(text);
      if (!ok) throw new Error("Réponse IA sans XML valide");
    } catch (err: any) {
      setError(
        "Erreur de génération : " +
          (err?.message || "Erreur inconnue") +
          ". Veuillez réessayer.",
      );
      setIsAiLoading(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          fontSize: "1.2rem",
          color: "#555",
        }}
      >
        Vérification en cours...
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: "1rem",
        }}
      >
        <p style={{ fontSize: "1.5rem", color: "#d32f2f", fontWeight: "bold" }}>
          Accès non autorisé
        </p>
        <p style={{ color: "#555", fontSize: "1rem" }}>
          Veuillez accéder à cet éditeur depuis l'application
          webADVANCED_PROCESS_MANAGEMENT.
        </p>
      </div>
    );
  }

  return (
    <>
      <FormManagerModalEnhanced manager={formManager} />

      {showFilterWarning && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9998,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              padding: "2rem 2.5rem",
              maxWidth: "520px",
              width: "90%",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "1.2rem",
                fontWeight: "bold",
                color: "#1976d2",
              }}
            >
              Aucun filtre détecté
            </p>
            <p
              style={{
                margin: 0,
                color: "#333",
                fontSize: "1rem",
                lineHeight: "1.6",
              }}
            >
              Ce diagramme ne contient pas de filtre. Un{" "}
              <strong>filtre par défaut</strong> a été ajouté automatiquement.
            </p>
            <div
              style={{
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                padding: "1rem",
                fontSize: "0.9rem",
                color: "#555",
                lineHeight: "1.7",
              }}
            >
              <p
                style={{
                  margin: "0 0 0.5rem",
                  fontWeight: "bold",
                  color: "#1976d2",
                }}
              >
                Comment ajouter un filtre dans votre diagramme JSON :
              </p>
              <p style={{ margin: 0 }}>
                Ajoutez une section{" "}
                <code
                  style={{
                    backgroundColor: "#e3f2fd",
                    padding: "0 4px",
                    borderRadius: "3px",
                  }}
                >
                  filters
                </code>{" "}
                dans votre JSON :
              </p>
              <pre
                style={{
                  backgroundColor: "#263238",
                  color: "#80cbc4",
                  padding: "0.8rem",
                  borderRadius: "6px",
                  fontSize: "0.8rem",
                  overflowX: "auto",
                  marginTop: "0.5rem",
                }}
              >
                {`"filterCategory": [{\n  "code": "mon-process-category",\n  "label": "Mes cas",\n  "rank": "1",\n  "icon": "Folder"\n}],\n"filters": [{\n  "code": "mon-process-filter",\n  "label": "Tous les cas",\n  "rank": "1",\n  "filterCategoryCode": "mon-process-category",\n  "condition": {},\n  "schema": {\n    "combinator": "and",\n    "rules": []\n  }\n}]`}
              </pre>
            </div>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => {
                  setShowFilterWarning(false);
                  if (pendingProcessData) {
                    pendingProcessData.forms = safeParseFormsField(
                      pendingProcessData.forms,
                    );
                    if (Object.keys(formsRef.current).length > 0)
                      pendingProcessData.forms = {
                        ...pendingProcessData.forms,
                        ...formsRef.current,
                      };
                    if (!pendingProcessData.forms)
                      pendingProcessData.forms = {};
                    if (!pendingProcessData.form) pendingProcessData.form = [];
                    if (!pendingProcessData.view) pendingProcessData.view = {};
                    if (!pendingProcessData.actions)
                      pendingProcessData.actions = {};
                    if (!pendingProcessData.activityActions)
                      pendingProcessData.activityActions = {};
                    if (!pendingProcessData.priorities)
                      pendingProcessData.priorities = [];
                    if (!pendingProcessData.title)
                      pendingProcessData.title =
                        pendingProcessData.label || pendingProcessData.code;
                    sendToWebADVANCED_PROCESS_MANAGEMENT(pendingProcessData);
                    setPendingProcessData(null);
                  }
                }}
                style={{
                  backgroundColor: "#1976d2",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "0.6rem 1.5rem",
                  fontSize: "1rem",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                J'ai compris
              </button>
            </div>
          </div>
        </div>
      )}

      {saveNotification.open && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
              padding: "2.5rem 3rem",
              minWidth: "380px",
              maxWidth: "480px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1rem",
              animation: "popIn 0.3s ease",
            }}
          >
            <p
              style={{
                margin: 0,
                fontWeight: "bold",
                fontSize: "1.4rem",
                color: "#1976d2",
                textAlign: "center",
              }}
            >
              {saveNotification.type === "success"
                ? "Sauvegarde réussie !"
                : "Erreur"}
            </p>
            <p
              style={{
                margin: 0,
                fontSize: "1rem",
                color: "#555",
                textAlign: "center",
                lineHeight: "1.5",
              }}
            >
              {saveNotification.message}
            </p>
            <button
              onClick={() =>
                setSaveNotification({ ...saveNotification, open: false })
              }
              style={{
                marginTop: "0.5rem",
                backgroundColor: "#1976d2",
                color: "white",
                border: "none",
                borderRadius: "6px",
                padding: "0.6rem 2rem",
                fontSize: "1rem",
                cursor: "pointer",
                fontWeight: "bold",
                boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
              }}
            >
              OK
            </button>
          </div>
          <style>{`@keyframes popIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        </div>
      )}

      {saveNotification.open &&
        (setTimeout(() => {
          setSaveNotification({ ...saveNotification, open: false });
        }, 6000) as any)}

      {isLoadingProcess && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999999999,
            color: "white",
            fontSize: "1.5rem",
            gap: "1rem",
          }}
        >
          <div
            style={{
              width: "50px",
              height: "50px",
              border: "5px solid rgba(255,255,255,0.3)",
              borderTop: "5px solid white",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <p style={{ margin: 0 }}>Génération en cours...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      <div className="content" id="js-drop-zone">
        <div
          className="message intro ai-section"
          style={{
            border: "2px solid #1565C0",
            borderRadius: "10px",
            padding: "14px 20px",
            background: "linear-gradient(135deg, #f0f7ff 0%, #e8f5e9 100%)",
          }}
        >
          <h3
            style={{
              margin: "0 0 10px 0",
              fontSize: "1.2rem",
              fontWeight: "700",
              color: "#1565C0",
              letterSpacing: "0.5px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            Générer avec Makina AI
          </h3>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              alignItems: "center",
              width: "100%",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            <textarea
              rows={18}
              placeholder="Décrivez votre processus métier étape par étape..."
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1.5px solid #1565C0",
                fontFamily: "inherit",
                fontSize: "13px",
                resize: "vertical",
                lineHeight: "1.5",
                boxSizing: "border-box",
                outline: "none",
                backgroundColor: "white",
              }}
            />
            <button
              onClick={handleAiGenerate}
              disabled={isAiLoading || !aiPrompt}
              style={{
                backgroundColor:
                  isAiLoading || !aiPrompt ? "#90CAF9" : "#1565C0",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "8px 24px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: isAiLoading || !aiPrompt ? "not-allowed" : "pointer",
                transition: "background-color 0.2s",
              }}
            >
              {isAiLoading ? "Génération..." : "Générer"}
            </button>
            {error && (
              <p style={{ color: "#d32f2f", margin: 0, fontSize: "13px" }}>
                {error}
              </p>
            )}
          </div>
        </div>

        <div className="message intro" style={{ padding: "10px 20px" }}>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "1.1rem",
              fontWeight: "600",
              color: "#1565C0",
            }}
          >
            Téléverser le fichier JSON
          </h3>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 16px",
              border: "2px dashed #1565C0",
              borderRadius: "8px",
              cursor: "pointer",
              backgroundColor: "#f0f7ff",
              fontSize: "13px",
              color: "#1565C0",
              fontWeight: "500",
            }}
          >
            Choisir un fichier JSON
            <input
              type="file"
              accept=".json"
              onChange={loadOldJson}
              style={{ display: "none" }}
            />
          </label>
        </div>

        <div className="message intro" style={{ padding: "10px 20px" }}>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "1.1rem",
              fontWeight: "600",
              color: "#1565C0",
            }}
          >
            Téléverser le fichier XML
          </h3>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 16px",
              border: "2px dashed #4CAF50",
              borderRadius: "8px",
              cursor: "pointer",
              backgroundColor: "#f1f8e9",
              fontSize: "13px",
              color: "#2E7D32",
              fontWeight: "500",
            }}
          >
            Choisir un fichier XML
            <input
              type="file"
              accept=".xml"
              onChange={loadMakinaXml}
              style={{ display: "none" }}
            />
          </label>
          <p
            style={{
              margin: "0 0 8px 0",
              fontSize: "12px",
              color: "#666",
              fontStyle: "italic",
            }}
          >
            ⚠️ Le fichier doit être un XML BPMN 2.0 valide (exporté depuis un
            outil de modélisation BPMN, ex. bpmn.io, Camunda Modeler).
          </p>
        </div>

        <div className="message intro" style={{ padding: "10px 20px" }}>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "1.1rem",
              fontWeight: "600",
              color: "#1565C0",
            }}
          >
            Nouveau diagramme BPMN
          </h3>
          <button
            onClick={() => newBpmnDiagram()}
            style={{
              backgroundColor: "white",
              color: "#1565C0",
              border: "2px solid #1565C0",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            + Créer un nouveau diagramme BPMN
          </button>
        </div>

        <div className="message error">
          <div className="note">
            <p>Ooops, we could not display the BPMN 2.0 diagram.</p>
            <div className="details">
              <span>cause of the problem</span>
              <pre></pre>
            </div>
          </div>
        </div>

        <div className="canvas" id="js-canvas"></div>
        <div className="properties" id="js-properties-panel"></div>

        <div
          id="js-properties-panel-readonly-banner"
          style={{
            display: "none",
            position: "fixed",
            bottom: 0,
            right: 0,
            width: "320px",
            padding: "8px 16px",
            backgroundColor: "#fff3e0",
            color: "#e65100",
            fontSize: "12px",
            fontWeight: "600",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            borderTop: "1px solid #ffe082",
            boxSizing: "border-box",
            zIndex: 20,
          }}
        >
          <span>🔒</span>
          <span>
            Lecture seule — rôle <strong>ADVANCED_PROCESS_MANAGEMENT</strong>{" "}
            requis pour modifier ce nœud.
          </span>
        </div>
      </div>

      {jsonData && (
        <ul className="buttons">
          <style>{`.buttons button { background-color: #1565C0; color: white; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 600; transition: background-color 0.2s; } .buttons button:hover { background-color: #0D47A1; }`}</style>
          <li>
            <button onClick={() => window.location.reload()}>← Retour</button>
          </li>
          <li>
            <button onClick={() => downloadBPMNXML()}>⬇ XML</button>
          </li>
          <li>
            <button onClick={() => downloadBPMNJson()}>⬇ JSON</button>
          </li>
          <li style={{ display: "none" }}>
            <button
              onClick={() => formManager.openManager()}
              style={{ backgroundColor: "#185FA5" }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: "6px", verticalAlign: "middle" }}
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
              </svg>
              Formulaires{" "}
              {formManager.formList.length > 0 &&
                `(${formManager.formList.length})`}
            </button>
          </li>
          {isFromExternalProcess && (
            <li>
              <button
                onClick={() => saveBPMNToADVANCED_PROCESS_MANAGEMENT()}
                style={{ backgroundColor: "#4CAF50" }}
              >
                {isCreateMode ? "Ajouter dans Makina" : "Sauvegarder"}
              </button>
            </li>
          )}
        </ul>
      )}
    </>
  );
};

export default AttachJsonPage;
